import React, { useState, useEffect, useCallback, useRef, useMemo, ChangeEvent } from "react";
import { EMBEDDED_POPUP_DONE, isEmbedded, openExternal } from "../../utils/shoplineEmbedded";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Checkbox,
  Tooltip,
  Switch,
  CircularProgress,
  IconButton,
  Typography,
  Stack,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Dialog,
  DialogContent,
  Button,
  Modal,
  TextField,
  InputAdornment
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PublicIcon from "@mui/icons-material/Public";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ocesha_logo from "../../assets/ocesha_images/ocesha_logo.png";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AIConciergePopup from "../../components/AIConciergePopup";
import RohanImg from "../../assets/podcast_monster/rohan_png.png";
import UserImg from "../../assets/podcast_monster/user_img.png";
import { useGetUserDetailsMutation } from "../../redux/api/authApi";
import { getCookie } from "utils/storageUtils";
import ComingSoonPopup from "../../components/ComingSoonPopup";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useAppSelector } from "../../redux/store";
import {
  usePlanDetailsMutation,
  useLazyGetProductQuery,
} from "../../redux/api/productApi";
import { isMobile } from "react-device-detect";
import ComputerIcon from '@mui/icons-material/Computer';
import unsplashImg from '../../assets/traffic_monster/unsplash_logo.png';
import SearchIcon from '@mui/icons-material/Search';
import unsplash_logo from '../../assets/traffic_monster/unsplash_logo.png';
import ConnectShopline from "pages/traffic_monster/campaigns/ConnectShopline";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";


// ─── Types ─────────────────────────────────────────────────────────────────

type TimingOption = "now" | "later";
type FollowupType = "ai" | "custom";
type Platform = "authority" | "linkedin" | "twitter" | "facebook" | "youtube";
type SocialPlatform = "linkedin" | "twitter" | "facebook" | "youtube";
type GoalOption = "awareness" | "engagement" | "leads" | "insights";

// ─── YouTube URL helper ─────────────────────────────────────────────────────
export interface YouTubeEmbedOptions {
  autoplay?: boolean;
  rel?: boolean;
  modestbranding?: boolean;
  start?: number;
  end?: number;
  cc?: boolean;
}

interface UnsplashImage {
  id: string;
  urls: {
    thumb: string;
    regular: string;
  };
  alt_description: string;
  width: number;
  height: number;
}

export function toYouTubeEmbed(
  url: string | null,
  opts: YouTubeEmbedOptions = {},
): string | null {
  if (!url) return null;
  let id: string | null = null;
  try {
    const u = new URL(url.trim());
    if (u.hostname.includes("youtu.be")) {
      id = u.pathname.slice(1) || null;
    }
    if (
      !id &&
      (u.hostname.includes("youtube.com") ||
        u.hostname.includes("www.youtube.com"))
    ) {
      if (u.searchParams.has("v")) {
        id = u.searchParams.get("v");
      } else {
        const embedMatch = u.pathname.match(/\/embed\/([^/?]+)/);
        const shortsMatch = u.pathname.match(/\/shorts\/([^/?]+)/);
        if (embedMatch) id = embedMatch[1];
        else if (shortsMatch) id = shortsMatch[1];
      }
    }
    if (!id) return null;
    const params = new URLSearchParams();
    if (opts.autoplay) params.set("autoplay", "1");
    if (opts.rel === false) params.set("rel", "0");
    else if (opts.rel === true) params.set("rel", "1");
    if (opts.modestbranding) params.set("modestbranding", "1");
    if (typeof opts.start === "number" && opts.start > 0)
      params.set("start", String(Math.floor(opts.start)));
    if (typeof opts.end === "number" && opts.end > 0)
      params.set("end", String(Math.floor(opts.end)));
    if (opts.cc) params.set("cc_load_policy", "1");
    const query = params.toString();
    return `https://www.youtube.com/embed/${encodeURIComponent(id)}${query ? `?${query}` : ""}`;
  } catch (_err) {
    return null;
  }
}

// ─── Constants ──────────────────────────────────────────────────────────────

const BASE_PLATFORMS: { id: Platform; label: string; icon: React.ReactNode; readOnly?: boolean; }[] = [
  {
    id: "authority",
    label: "Profile Page (Public Profile)",
    icon: <PublicIcon sx={{ color: "#7c8cf8", fontSize: 22 }} />,
    readOnly: true,
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    icon: <LinkedInIcon sx={{ color: "#0a66c2", fontSize: 22 }} />,
  },
  {
    id: "twitter",
    label: "X (Twitter)",
    icon: <TwitterIcon sx={{ color: "#4BA5EB", fontSize: 22 }} />,
  },
  {
    id: "facebook",
    label: "Facebook",
    icon: <FacebookIcon sx={{ color: "#1877f2", fontSize: 22 }} />,
  },
  {
    id: "youtube",
    label: "YouTube",
    icon: <YouTubeIcon sx={{ color: "#ff0000", fontSize: 22 }} />,
  },
];

const PREVIEW_TABS: { id: Platform; label: string }[] = [
  { id: "linkedin", label: "LinkedIn" },
  { id: "twitter", label: "X (Twitter)" },
  { id: "facebook", label: "Facebook" },
  { id: "youtube", label: "YouTube" },
  { id: "authority", label: "Profile Page" },
];

const SOCIAL_PLATFORMS: SocialPlatform[] = ["linkedin", "twitter", "facebook", "youtube"];

// Not available yet: these can't be connected or selected, clicking them shows the
// "Coming Soon" popup (same rule as pages/traffic_monster/campaigns/socialPost.tsx).
const COMING_SOON_PLATFORMS: Platform[] = ["twitter", "facebook"];
const isComingSoonPlatform = (id: string): boolean => (COMING_SOON_PLATFORMS as string[]).includes(id);

// ─── Plan-based platform gating ────────────────────────────────────────────
// FREE:    Authority Page only.
// STARTER: Authority Page + exactly one social platform at a time.
// GROWTH / PRO: Authority Page + all social platforms.
type PlanTier = "FREE" | "STARTER" | "GROWTH" | "PRO";

const SUBSCRIPTION_LEVEL_TO_PLAN_TIER: Record<string, PlanTier> = {
  level_0a: "FREE",
  level_4: "STARTER",
  level_5: "STARTER",
  level_0: "GROWTH",
  level_2: "GROWTH",
  level_1: "PRO",
  level_3: "PRO",
};

const getPlanTier = (subscriptionLevel: string | null | undefined): PlanTier =>
  (subscriptionLevel && SUBSCRIPTION_LEVEL_TO_PLAN_TIER[subscriptionLevel]) || "FREE";

// Platforms that show in the top "Connections" row (no authority — it's in-house)
const CONNECTABLE_PLATFORMS: {
  id: SocialPlatform;
  label: string;
  shortLabel: string;
  icon: React.ReactNode;
  brandColor: string;
}[] = [
    { id: "linkedin", label: "LinkedIn", shortLabel: "LinkedIn", icon: <LinkedInIcon sx={{ fontSize: 18 }} />, brandColor: "#0a66c2" },
    { id: "twitter", label: "X (Twitter)", shortLabel: "X", icon: <TwitterIcon sx={{ fontSize: 18 }} />, brandColor: "#4BA5EB" },
    { id: "facebook", label: "Facebook", shortLabel: "Facebook", icon: <FacebookIcon sx={{ fontSize: 18 }} />, brandColor: "#1877f2" },
    { id: "youtube", label: "YouTube", shortLabel: "YouTube", icon: <YouTubeIcon sx={{ fontSize: 18 }} />, brandColor: "#ff0000" },
  ];

// ─── Styles ─────────────────────────────────────────────────────────────────

const S = {
  page: {
    background: "#0F1117",
    minHeight: "100vh",
    color: "#E8EAF0",
    fontFamily: "'Inter', -apple-system, 'DM Sans', 'Segoe UI', sans-serif",
    display: "flex",
    flexDirection: "row" as const,
  },
  sidebar: {
    width: 260,
    minWidth: 260,
    background: "linear-gradient(180deg, #0B1220 0%, #06080F 100%)",
    borderRight: "1px solid rgba(255,255,255,0.06)",
    minHeight: "100vh",
    height: "100vh",
    display: "flex",
    flexDirection: "column" as const,
    position: "sticky" as const,
    top: 0,
    alignSelf: "flex-start" as const,
    zIndex: 20,
    overflowY: "auto" as const,
    overflowX: "hidden" as const,
  },
  sidebarLogo: {
    padding: "18px 20px 14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sidebarMenu: { flex: 1, padding: "4px 0 24px 0" },
  sidebarItem: (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 24px",
    fontSize: 13.5,
    fontWeight: active ? 600 : 500,
    color: active ? "#fff" : "rgba(255,255,255,0.7)",
    background: active ? "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)" : "transparent",
    borderRadius: "8px",
    margin: "1px 12px",
    cursor: "pointer",
    transition: "all 0.15s",
    userSelect: "none" as const,
  }),
  sidebarDivider: { borderColor: "rgba(255,255,255,0.06)", margin: "8px 16px" },
  mainContent: { flex: 1, display: "flex", flexDirection: "column" as const, minWidth: 0, background: "#0F1117" },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 24px",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    background: "#15151D",
    position: "sticky" as const,
    top: 0,
    zIndex: 10,
  },
  backBtn: {
    color: "#fff", fontSize: "14px", fontWeight: 600,
    display: "flex", alignItems: "center", gap: "6px",
    cursor: "pointer", background: "#007bff", border: "none",
    padding: "8px 14px", borderRadius: "6px", transition: "0.2s ease",
  },
  connectionsBar: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "14px 32px",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    background: "#15151D",
    flexWrap: "wrap" as const,
  },
  connectionsLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
    color: "rgba(255,255,255,0.5)", textTransform: "uppercase" as const,
    marginRight: 6,
  },
  connectChip: (connected: boolean, brandColor: string, isHovered: boolean) => ({
    display: "flex", alignItems: "center", gap: 9,
    padding: "7px 14px 7px 10px", borderRadius: 99,
    border: connected
      ? `1.5px solid ${brandColor}`
      : "1.5px dashed rgba(255,255,255,0.18)",
    background: connected
      ? isHovered ? `${brandColor}25` : `${brandColor}18`
      : isHovered ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.02)",
    fontSize: 12, fontWeight: 600,
    color: connected ? "#E8EAF0" : "rgba(255,255,255,0.7)",
    cursor: "pointer",
    transition: "all 0.18s ease",
    whiteSpace: "nowrap" as const,
    transform: isHovered ? "translateY(-1px)" : "translateY(0)",
    boxShadow: connected && isHovered ? `0 4px 12px ${brandColor}30` : "none",
  }),
  body: { display: "flex", flex: 1, gap: 0, padding: "0", overflow: "hidden" },
  leftPanel: {
    width: "46%", minWidth: 400,
    borderRight: "1px solid rgba(255,255,255,0.07)",
    overflowY: "auto" as const,
    padding: "28px 32px",
    display: "flex", flexDirection: "column" as const, gap: 28,
    background: "#0F1117",
  },
  rightPanel: {
    flex: 1, overflowY: "auto" as const,
    padding: "28px 32px",
    display: "flex", flexDirection: "column" as const, gap: 24,
    background: "#0F1117",
  },
  pageHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between" },
  closeBtn: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8, color: "rgba(255,255,255,0.5)",
    cursor: "pointer", padding: "6px 8px",
    display: "flex", alignItems: "center",
    transition: "background 0.15s",
  },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: "#E8EAF0", marginBottom: 4 },
  sectionSub: { fontSize: 12, color: "rgba(255,255,255,0.45)", marginBottom: 14 },
  platformCard: (selected: boolean, isAuthority: boolean, isDisabled: boolean) => ({
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 14px", borderRadius: 10,
    border: selected ? "1.5px solid #4BA5EB" : "1.5px solid rgba(255,255,255,0.07)",
    background: selected
      ? isAuthority ? "rgba(75,165,235,0.12)" : "rgba(75,165,235,0.08)"
      : "#15151D",
    marginBottom: 8,
    cursor: isDisabled ? "not-allowed" : "pointer",
    transition: "all 0.15s",
    position: "relative" as const,
    opacity: isDisabled ? 0.5 : 1,
  }),
  platformLabel: { fontSize: 13, fontWeight: 600, color: "#E8EAF0", flex: 1 },
  platformHandle: { fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 1 },
  automaticBadge: {
    fontSize: 10, fontWeight: 700, color: "#4BA5EB",
    background: "rgba(75,165,235,0.15)", border: "1px solid rgba(75,165,235,0.3)",
    padding: "2px 8px", borderRadius: 99, letterSpacing: "0.04em",
  },
  notConnectedBadge: {
    fontSize: 10, fontWeight: 700, color: "#f59e0b",
    background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)",
    padding: "2px 8px", borderRadius: 99, letterSpacing: "0.04em",
  },
  selectAllRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 },
  timingCard: (active: boolean) => ({
    display: "flex", alignItems: "flex-start", gap: 10,
    padding: "12px 14px", borderRadius: 10,
    border: active ? "1.5px solid #4BA5EB" : "1.5px solid rgba(255,255,255,0.07)",
    background: active ? "rgba(75,165,235,0.08)" : "#15151D",
    marginBottom: 8, cursor: "pointer", transition: "all 0.15s",
    overflow: "hidden", minWidth: 0,
  }),
  dateTimeRow: { display: "flex", gap: 10, marginTop: 12 },
  dateInput: {
    flex: 1, background: "rgba(255,255,255,0.04)",
    border: "1.5px solid rgba(255,255,255,0.1)", borderRadius: 8,
    padding: "9px 12px", color: "#E8EAF0", fontSize: 13,
    display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
  },
  timezoneRow: {
    marginTop: 10, display: "flex", alignItems: "center", gap: 6,
    fontSize: 12, color: "rgba(255,255,255,0.45)",
    background: "rgba(255,255,255,0.04)", borderRadius: 8,
    padding: "8px 12px", border: "1px solid rgba(255,255,255,0.07)",
  },
  optionCard: (checked: boolean) => ({
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "12px 14px", borderRadius: 10,
    border: checked ? "1.5px solid #4BA5EB" : "1.5px solid rgba(255,255,255,0.07)",
    background: "#15151D", marginBottom: 8,
    cursor: "pointer", transition: "all 0.15s",
  }),
  badge: (color: string, bg: string, border: string) => ({
    fontSize: 10, fontWeight: 700, color, background: bg,
    border: `1px solid ${border}`, padding: "2px 8px",
    borderRadius: 99, letterSpacing: "0.04em", marginLeft: 8,
  }),
  previewCard: { background: "#15151D", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, overflow: "hidden" as const },
  previewHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px" },
  previewAvatar: {
    width: 36, height: 36, borderRadius: "50%",
    background: "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 700, fontSize: 14, color: "#fff", flexShrink: 0,
  },
  videoThumb: {
    position: "relative" as const, background: "#0A0A12",
    aspectRatio: "16/9",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", overflow: "hidden" as const,
  },
  followupCard: { background: "#15151D", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "18px 20px" },
  followupOption: (active: boolean) => ({
    flex: 1, padding: "14px", borderRadius: 10,
    border: active ? "1.5px solid #4BA5EB" : "1.5px solid rgba(255,255,255,0.07)",
    background: active ? "rgba(75,165,235,0.08)" : "rgba(255,255,255,0.03)",
    cursor: "pointer", transition: "all 0.15s",
  }),
  bottomBar: {
    display: "flex", alignItems: "center", justifyContent: "flex-end",
    gap: 12, padding: "16px 32px",
    borderTop: "1px solid rgba(255,255,255,0.07)",
    background: "#15151D",
    position: "sticky" as const, bottom: 0,
  },
  cancelBtn: {
    color: "rgba(255,255,255,0.6)",
    border: "1.5px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "8px 24px",
    textTransform: "none" as const, fontWeight: 600, fontSize: 13,
    background: "transparent", cursor: "pointer",
  },
  publishBtn: {
    background: "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)",
    color: "#fff", borderRadius: "8px 0 0 8px",
    padding: "9px 20px", textTransform: "none" as const,
    fontWeight: 700, fontSize: 13,
    display: "flex", alignItems: "center", gap: 8, boxShadow: "none",
  },
  publishDropBtn: {
    background: "#3a94da", color: "#fff",
    borderRadius: "0 8px 8px 0", padding: "9px 10px",
    minWidth: "auto", boxShadow: "none",
  },
  dropMenu: {
    position: "absolute" as const,
    bottom: "calc(100% + 8px)", right: 0,
    background: "#15151D", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10, overflow: "hidden" as const,
    minWidth: 160, boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
    zIndex: 99,
  },
  dropMenuItem: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "11px 16px", fontSize: 13,
    color: "rgba(255,255,255,0.75)", cursor: "pointer",
    transition: "background 0.12s",
  },
  editInput: {
    background: "rgba(255,255,255,0.04)",
    border: "1.5px solid rgba(255,255,255,0.1)",
    borderRadius: 8, color: "#E8EAF0", fontSize: 13,
    padding: "8px 10px", width: "100%", outline: "none",
    fontFamily: "'Inter', -apple-system, 'DM Sans', 'Segoe UI', sans-serif",
    resize: "vertical" as const,
  },
};

// ─── Main Component ─────────────────────────────────────────────────────────

interface PublishScheduleClipProps {
  onBack?: () => void;
  onClose?: () => void;
  onPublish?: (data: any) => void;
  clipTitle?: string;
  clipThumbnail?: string;
  userName?: string;
}

// Turns a stored video path (e.g. "stt_uploads/<id>/video.mp4") into a playable absolute URL.
// Leaves http(s), protocol-relative, blob: and data: URLs untouched.
const getFullVideoUrl = (path?: string | null): string => {
  if (!path) return "";
  if (/^(https?:|blob:|data:|\/\/)/i.test(path)) return path;
  const base = (process.env.REACT_APP_VIDEO_SERVICE || "").replace(/\/+$/, "");
  return `${base}/${path.replace(/^\/+/, "")}`;
};

const PublishScheduleClip: React.FC<PublishScheduleClipProps> = ({
  onBack,
  onClose,
  onPublish,
  clipTitle: clipTitleProp = "AI ISN'T JUST HYPE. IT'S IMPACT.",
  clipThumbnail: clipThumbnailProp = "",
  userName = "",
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const navState = (location.state as any) || {};

  // console.log('navstate res=>', navState);

  const sourceType = navState.sourceType ||
    (navState.type === 'image' ? 'image' :
      navState.type === 'social' ? 'social' :
        navState.type === 'socialpost' ? 'socialpost' :
          navState.type === 'course' ? 'course' :
            navState.type === 'episode' ? 'episode' :
              navState.type === 'blog' ? 'blog' : 'clip');// 'blog' | 'course' | 'clip' | 'episode'
  const allInitialSuggestions = navState.allSuggestions || [];

  const clipTitle = navState.clipTitle || clipTitleProp;
  const clipThumbnail = navState.clipThumbnail || clipThumbnailProp;
  const postId =
    navState.postId ||
    navState.muid ||
    navState.guid ||
    navState.sid ||
    navState.id ||
    navState.clipId ||
    navState.clip_id ||
    navState.post_id ||
    localStorage.getItem('postId') ||
    localStorage.getItem('post_id') ||
    localStorage.getItem('clipPostId') ||
    localStorage.getItem('muid') ||
    localStorage.getItem('guid') ||
    localStorage.getItem('sid') ||
    '';
  const userEmail = navState.userEmail || localStorage.getItem('user_email') || '';

  const handleBack = onBack ?? (() => navigate(-1));
  const handleClose = onClose ?? (() => navigate(-1));

  const BASE_URL = process.env.REACT_APP_SERVER_ENDPOINT || '';

  // ── State ─────────────────────────────────────────────────────────────────
  const [authStatus, setAuthStatus] = useState<Record<string, any>>({});
  const [connectionState, setConnectionState] = useState<Record<SocialPlatform, boolean>>({
    linkedin: false,
    twitter: false,
    facebook: false,
    youtube: false,
  });
  const [connectionDetails, setConnectionDetails] = useState<Record<string, any>>({});
  const [connectingPlatform, setConnectingPlatform] = useState<SocialPlatform | null>(null);
  const [hoveredChip, setHoveredChip] = useState<SocialPlatform | null>(null);
  const [getUserDetailsMutation] = useGetUserDetailsMutation();
  const [userDets, setUserDets] = useState<any>();
  const [connectionLoaded, setConnectionLoaded] = useState(false);

  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [comingSoonPlatform, setComingSoonPlatform] = useState("");

  const [choosingImageModal, setChoosingImageModal] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [openUnsplash, setOpenUnsplash] = useState<any>();
  const [query, setQuery] = useState('');
  const [unsplashSearch, setUnsplashSearch] = useState<boolean>(false);
  const [selectedUnsplashImg, setSelectedUnsplashImg] = useState(null);
  const [images, setImages] = useState<UnsplashImage[]>([]);


  const useremail = localStorage.getItem("user_email") || "";
  const planTier = getPlanTier(localStorage.getItem("subscription_level"));
  const fetchData = async () => {
    try {
      const response = await getUserDetailsMutation({
        email: useremail || getCookie("user_email") || "",
        filter: "",
        start_date: "",
        end_date: "",
      });
      if ("data" in response) {
        setUserDets(response.data.data);
      }
    } catch (error) { }
  };

  /* useEffect(() => {
    fetchData();
  }, []); */
  useEffect(() => {
    fetchData();

    const handleProfileUpdated = () => {
      fetchData();
    };

    window.addEventListener("profileUpdated", handleProfileUpdated);

    return () => {
      window.removeEventListener(
        "profileUpdated",
        handleProfileUpdated
      );
    };
  }, []);


  // Fetch user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const email = localStorage.getItem('user_email') || '';
        const { data } = await axios.get(`${BASE_URL}/api/users/me`, {
          params: email ? { user_email: email } : {},
          withCredentials: true,
        });
        if (data?.user && typeof data.user === 'object') {
          setAuthStatus(data.user);
        }
      } catch (err) {
        console.error("Failed to fetch user info", err);
      }
    };
    fetchUserInfo();
  }, [BASE_URL]);

  // Fetch connection status
  const fetchConnectionStatus = useCallback(async () => {
    try {
      const email = localStorage.getItem('user_email') || '';
      const { data } = await axios.get(
        `${BASE_URL}/api/socialmedia/api/auth-status`,
        { params: { user_email: email }, withCredentials: true }
      );
      if (data?.status) {
        setConnectionState({
          linkedin: !!data.status.linkedin,
          twitter: !!data.status.twitter,
          facebook: !!data.status.facebook,
          youtube: !!data.status.youtube,
        });
        setConnectionLoaded(true);
      }
      if (data?.details) {
        setConnectionDetails(data.details);
      }
    } catch (err) {
      console.error("Failed to fetch connection status", err);
    }
  }, [BASE_URL]);

  useEffect(() => {
    fetchConnectionStatus();
  }, [fetchConnectionStatus]);

  // ── Token health: is each connected platform's connection still valid? ──
  // Checked when the screen opens (badge on the platform card) and again when
  // the user clicks Publish / Schedule, so an expired connection is reported
  // right away instead of failing later in the background scheduler.
  const [connectionIssues, setConnectionIssues] = useState<Record<string, { status: string; message: string }>>({});
  const checkConnections = useCallback(async (platformIds: string[]) => {
    const ids = platformIds.filter((p) => p === "facebook" || p === "linkedin");
    if (!ids.length) return {} as Record<string, { status: string; message: string }>;
    try {
      const email = localStorage.getItem('user_email') || '';
      const { data } = await axios.get(`${BASE_URL}/api/socialmedia/api/connections/check`, {
        params: { user_email: email, platforms: ids.join(",") }, withCredentials: true,
      });
      const issues: Record<string, { status: string; message: string }> = {};
      Object.entries(data?.results || {}).forEach(([p, r]: [string, any]) => {
        if (r && r.ok === false && r.status !== "not_connected") issues[p] = { status: r.status, message: r.message };
      });
      setConnectionIssues((prev) => {
        const next = { ...prev };
        ids.forEach((p) => { delete next[p]; });
        return { ...next, ...issues };
      });
      return issues;
    } catch (err) {
      console.warn("Connection check failed; continuing", err);   // never block on a failed check
      return {} as Record<string, { status: string; message: string }>;
    }
  }, [BASE_URL]);

  useEffect(() => {
    if (!connectionLoaded) return;
    const connected = (["facebook", "linkedin"] as string[]).filter((p) => (connectionState as any)[p]);
    if (connected.length) checkConnections(connected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionLoaded, connectionState.facebook, connectionState.linkedin]);

  // Detect OAuth callback returns
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const platforms: SocialPlatform[] = ["linkedin", "twitter", "facebook", "youtube"];
    let detected: SocialPlatform | null = null;
    for (const p of platforms) {
      if (params.get(p) === "connected") {
        detected = p;
        break;
      }
    }
    if (detected) {
      toast.success(`${CONNECTABLE_PLATFORMS.find(p => p.id === detected)?.label} connected!`);
      params.delete(detected);
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState({}, '', newUrl);
      fetchConnectionStatus();
    }
  }, [fetchConnectionStatus]);

  // SHOPLINE popup finished (e.g. ".../generated-blogs?linkedin=connected"): stay here.
  useEffect(() => {
    const onPopupDone = (e: Event) => {
      const path: string = (e as CustomEvent)?.detail?.path || "";
      let params: URLSearchParams;
      try { params = new URL(path, window.location.origin).searchParams; } catch { params = new URLSearchParams(); }
      const done = CONNECTABLE_PLATFORMS.find((p) => params.get(p.id) === "connected");
      if (done) toast.success(`${done.label} connected!`);
      fetchConnectionStatus();
    };
    window.addEventListener(EMBEDDED_POPUP_DONE, onPopupDone);
    return () => window.removeEventListener(EMBEDDED_POPUP_DONE, onPopupDone);
  }, [fetchConnectionStatus]);

  // Connect a platform via OAuth.
  // Before redirecting away, stash the current route + navState in sessionStorage
  // so the OAuth landing page can bounce the user back here afterward, with all
  // the original navigation context (postId, clipTitle, etc.) restored.
  const handleConnect = async (platform: SocialPlatform) => {
    setConnectingPlatform(platform);
    try {
      sessionStorage.setItem(
        "oauth_return",
        JSON.stringify({
          path: window.location.pathname,
          state: navState,
          platform,
          timestamp: Date.now(),
        })
      );

      // Inside SHOPLINE: social logins can't load in the iframe → popup.
      // stay: true keeps this page (and the post being edited) instead of reloading
      // the iframe on the dashboard; EMBEDDED_POPUP_DONE below refreshes the status.
      if (isEmbedded()) {
        sessionStorage.removeItem("oauth_return");
        await openExternal({ social: platform, stay: true });
        setConnectingPlatform(null);
        return;
      }

      const { data } = await axios.get(
        `${BASE_URL}/api/socialmedia/auth/${platform}`,
        { withCredentials: true }
      );
      if (data?.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error(`Could not start ${platform} authentication.`);
        setConnectingPlatform(null);
        sessionStorage.removeItem("oauth_return");
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || "Connection failed";
      toast.error(`${platform}: ${detail}`);
      setConnectingPlatform(null);
      sessionStorage.removeItem("oauth_return");
    }
  };

  // Disconnect a platform
  const handleDisconnect = async (platform: SocialPlatform) => {
    if (!window.confirm(`Disconnect ${platform}?`)) return;
    try {
      await axios.post(
        `${BASE_URL}/api/socialmedia/api/auth/disconnect/${platform}`,
        {},
        { withCredentials: true }
      );
      toast.success(`${platform} disconnected.`);
      setSelectedPlatforms(prev => prev.filter(p => p !== platform));
      fetchConnectionStatus();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || "Disconnect failed";
      toast.error(detail);
    }
  };

  const PLATFORMS: { id: Platform; label: string; handle?: string; icon: React.ReactNode; readOnly?: boolean; }[] = BASE_PLATFORMS.map((p) => {
    if (p.id === 'authority') return { ...p, handle: undefined };
    const details = connectionDetails[p.id];
    let handle: string | undefined;
    if (details) {
      handle = details.screen_name || details.page_name || details.name || details.urn;
    }
    if (!handle) {
      const perPlatform = authStatus[p.id];
      if (perPlatform && typeof perPlatform === 'object') {
        handle = perPlatform.handle || perPlatform.username || perPlatform.name || perPlatform.email;
      }
    }
    if (!handle) {
      handle = authStatus.username || authStatus.name || authStatus.email || undefined;
    }
    return { ...p, handle };
  });

  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>(() => {
    const incoming = (navState.platform || "").trim();

    if (!incoming) {
      return ["authority"];
    }

    const platforms = incoming
      .split("|")
      .map((p: string) => p.trim().toLowerCase() as Platform)
      .filter((p: Platform) => !isComingSoonPlatform(p));

    return platforms.includes("authority")
      ? platforms
      : ["authority", ...platforms];
  });

  // Auto-remove disconnected platforms from selection
  useEffect(() => {
    if (!connectionLoaded) return;

    setSelectedPlatforms(prev =>
      prev.filter(p => {
        if (p === "authority") return true;
        if (isComingSoonPlatform(p)) return false;
        return connectionState[p as SocialPlatform];
      })
    );
  }, [connectionLoaded, connectionState]);

  // Parse incoming schedule_time from navState (set by SocialPost page).
  // The API can send schedule_time WITHOUT a timezone suffix (e.g.
  // "2026-08-08T03:30:00"), which is UTC but `new Date()` would otherwise
  // misread as local time — so append "Z" when no offset is present.
  // Only honored if it's a valid date/time in the future — a past scheduled
  // time is treated as if none was passed, so the UI falls back to defaults.
  const incomingScheduleRaw = (() => {
    const raw = navState.scheduleTime;
    if (!raw) return null;
    const hasTz = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(raw);
    const d = new Date(hasTz ? raw : raw + "Z");
    if (isNaN(d.getTime())) return null;
    if (d.getTime() < Date.now()) return null; // ignore past scheduled times
    return d;
  })();

  const incomingSchedule = (() => {
    if (!incomingScheduleRaw) return { date: "", time: "", isoDate: "", time24: "" };
    const d = incomingScheduleRaw;
    const date = d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); // e.g. "May 15, 2025"
    const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }); // e.g. "10:00 AM"
    const isoDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; // for <input type="date">
    const time24 = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; // for <input type="time">
    return { date, time, isoDate, time24 };
  })();

  const [timing, setTiming] = useState<TimingOption>(incomingSchedule.date ? "later" : "now");
  const [scheduleDate, setScheduleDate] = useState(() => {
    if (incomingSchedule.date) return incomingSchedule.date;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  });
  const [scheduleTime, setScheduleTime] = useState(incomingSchedule.time || "10:00 AM");
  const [scheduleTimezone, setScheduleTimezone] = useState("UTC");
  const [customizePerPlatform, setCustomizePerPlatform] = useState(true);
  const [addFollowup, setAddFollowup] = useState(true);
  const [previewTab, setPreviewTab] = useState<Platform>("linkedin");
  const [followupEnabled, setFollowupEnabled] = useState(true);
  const [followupType, setFollowupType] = useState<FollowupType>("ai");
  const [showDropMenu, setShowDropMenu] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [goal, setGoal] = useState<GoalOption>("awareness");
  const [toneOfVoice, setToneOfVoice] = useState<string>("Professional");
  const [keyMessage, setKeyMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string>("");

  const [isEditingPreview, setIsEditingPreview] = useState(false);
  const [showEditHint, setShowEditHint] = useState(true);
  const [isFetchingPost, setIsFetchingPost] = useState(false);
  const [editTitle, setEditTitle] = useState(clipTitle || "");
  const [editContent, setEditContent] = useState(navState.postContent || "");
  const [editBlogUrl, setEditBlogUrl] = useState(navState.source_url || "");
  const [editVideoUrl, setEditVideoUrl] = useState(navState.video_url || "");
  const [editThumbnailUrl, setEditThumbnailUrl] = useState(clipThumbnail || "");

  // ── Multi-image posts (Facebook / LinkedIn) ──
  // Images the merchant can add next to the thumbnail (SHOPLINE product images),
  // and the extra images currently selected. Thumbnail = cover (first image).
  const MAX_EXTRA_IMAGES = 8;
  const availableImages: string[] = useMemo(() => {
    const out: string[] = [];
    [...(navState.product_images || []), ...(navState.image_urls || [])].forEach((u: any) => {
      if (typeof u === "string" && u.trim() && !out.includes(u.trim())) out.push(u.trim());
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [extraImages, setExtraImages] = useState<string[]>(() => {
    const cover = navState.clipThumbnail || clipThumbnailProp || "";
    const saved: string[] = (navState.image_urls || []).filter((u: any) => typeof u === "string" && u && u !== cover);
    if (saved.length) return saved.slice(0, MAX_EXTRA_IMAGES);
    return availableImages.filter((u) => u !== cover).slice(0, 3);
  });
  const toggleExtraImage = (u: string) =>
    setExtraImages((prev) =>
      prev.includes(u) ? prev.filter((x) => x !== u)
        : prev.length >= MAX_EXTRA_IMAGES ? prev : [...prev, u]);
  // Sent to the backend: cover first, then the selected extra images
  const postImageUrls = (): string[] | undefined => {
    if (!availableImages.length) return undefined;
    const out: string[] = [];
    [editThumbnailUrl || clipThumbnail, ...extraImages].forEach((u) => {
      if (u && !out.includes(u)) out.push(u);
    });
    return out.slice(0, MAX_EXTRA_IMAGES + 1);
  };
  const [fetchedPostData, setFetchedPostData] = useState<any>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [savingPost, setSavingPost] = useState(false);

  // ── Live / draft versions ─────────────────────────────────────────────────
  // While a post is live, edits are saved as a DRAFT and the live post (profile page,
  // social posts) is not touched. Publishing promotes the draft to be the live version.
  const [liveVersion, setLiveVersion] = useState<any>(null);
  const [draftVersion, setDraftVersion] = useState<any>(null);
  const [versionView, setVersionView] = useState<"draft" | "live">("draft");
  const workingCopyRef = useRef<any>(null);
  const hasDraft = !!draftVersion;
  const isViewingLive = hasDraft && versionView === "live";

  const snapshotFromPost = (post: any) => ({
    title: post?.suggestion_title || post?.title || "",
    content: post?.content || "",
    image_url: post?.image_url || "",
    image_urls: Array.isArray(post?.image_urls) ? post.image_urls : [],
    video_url: post?.video_url || "",
    url: post?.url || "",
  });

  const getEditorSnapshot = () => ({
    title: editTitle,
    content: editContent,
    image_url: editThumbnailUrl,
    image_urls: [editThumbnailUrl, ...extraImages].filter(Boolean),
    video_url: editVideoUrl,
    url: editBlogUrl,
  });

  const applySnapshot = (snap: any, full: boolean = true) => {
    setEditTitle(snap?.title || "");
    setEditContent(snap?.content || "");
    setEditThumbnailUrl(snap?.image_url || "");
    setEditVideoUrl(snap?.video_url || "");
    if (full) {
      setEditBlogUrl(snap?.url || "");
      if (Array.isArray(snap?.image_urls) && snap.image_urls.length) {
        const cover = snap.image_url || snap.image_urls[0];
        setExtraImages(snap.image_urls.filter((u: any) => u && u !== cover).slice(0, MAX_EXTRA_IMAGES));
      }
    }
  };

  const switchVersion = (view: "draft" | "live") => {
    if (view === versionView || !hasDraft) return;
    setIsEditingPreview(false);
    if (view === "live") {
      workingCopyRef.current = getEditorSnapshot();   // keep unsaved edits while looking at live
      applySnapshot(liveVersion);
    } else {
      applySnapshot(workingCopyRef.current || draftVersion);
    }
    setVersionView(view);
  };


  // ── Blog suggestions state ────────────────────────────────────────────────
  const [blogSuggestions, setBlogSuggestions] = useState<any[]>([]);
  const [isFetchingBlogs, setIsFetchingBlogs] = useState(false);
  const [selectedBlogId, setSelectedBlogId] = useState<string | null>(null);
  const [showBlogSuggestions, setShowBlogSuggestions] = useState(false);

  const [upgradeModal, setUpgradeModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const genieSelector = useAppSelector((state) => state.genieState);
  const [getProduct] = useLazyGetProductQuery();

  const selectedCampaign =
    localStorage.getItem("campId") ||
    localStorage.getItem("publishingCampId") ||
    "";

  const resolvedUserName =
    authStatus?.name ||
    authStatus?.linkedin?.name ||
    authStatus?.facebook?.name ||
    authStatus?.youtube?.name ||
    authStatus?.twitter?.name ||
    userName ||
    "User";

  const initials = resolvedUserName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase();

  useEffect(() => {
    /* if (clipThumbnail) {
      const absoluteUrl = clipThumbnail.startsWith('http')
        ? clipThumbnail
        : `${BASE_URL}${clipThumbnail.startsWith('/') ? '' : '/'}${clipThumbnail}`;
      setEditVideoUrl(absoluteUrl);
    } */
    if (clipThumbnail) {
      const absoluteUrl = clipThumbnail.startsWith("http")
        ? clipThumbnail
        : `${BASE_URL}${clipThumbnail}`;

      setEditThumbnailUrl(absoluteUrl);
    }

    if (navState?.video_url) {
      setEditVideoUrl(navState.video_url);
    }
    if (navState.postContent) {
      setEditContent(navState.postContent);
    }
    if (navState.blogUrl) {

      // BLOGS → keep old behavior
      if (sourceType !== 'course') {
        const cleanUrl = (navState.blogUrl || '').replace(/\/\/$/, '');
        //setEditBlogUrl(cleanUrl);
        return;
      }

      // COURSES → new public URL conversion
      const convertCourseUrl = async () => {
        try {
          const incomingUrl = navState.blogUrl || '';

          // already correct
          if (incomingUrl.includes('ocesha.ai')) {
            //setEditBlogUrl(incomingUrl.replace(/\/\/$/, ''));
            return;
          }

          const match = incomingUrl.match(/courseId=([^&]+)/);
          const extractedCourseId = match?.[1] || postId;

          const email = localStorage.getItem('user_email') || '';

          const taskRes = await axios.get(
            `${BASE_URL}/api/prompts/task_status`,
            {
              params: {
                task_id: extractedCourseId,
                user_email: email,
              },
              withCredentials: true,
            }
          );

          const taskData =
            taskRes.data?.result?.data ||
            taskRes.data?.data ||
            taskRes.data ||
            {};

          const profileId =
            taskData.profile_id ||
            taskData.user_id ||
            localStorage.getItem('profile_id') ||
            localStorage.getItem('user_id') ||
            email;

          const rawTitle =
            taskData.course_title ||
            taskData.title ||
            clipTitle ||
            'course';

          const titleSlug = rawTitle
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-');

          const finalCourseUrl =
            `{process.env.REACT_APP_LOGOUT_DOMAIN || "https://ocesha.ai"}/${encodeURIComponent(profileId)}/course/${titleSlug}`;

          //setEditBlogUrl(finalCourseUrl);

        } catch (err) {
          console.error(err);

          // fallback
          const fallbackTitle = (clipTitle || 'course')
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-');

          const fallbackProfile =
            localStorage.getItem('profile_id') ||
            localStorage.getItem('user_id') ||
            localStorage.getItem('user_email') ||
            'user';

          // setEditBlogUrl(
          //   `https://ocesha.ai/${encodeURIComponent(fallbackProfile)}/course/${fallbackTitle}`
          // );
        }
      };

      convertCourseUrl();
    }
    // Auto-populate suggestions passed from listing page (course/clip/episode)
    if (allInitialSuggestions.length > 0) {
      setBlogSuggestions(allInitialSuggestions);
      setShowBlogSuggestions(true);

      // For clip/episode only: pre-fill from suggestion adaptation/hook
      // For course: title and description come directly from navState (course object) — no override needed
      if (sourceType === 'clip' || sourceType === 'episode') {
        const best = allInitialSuggestions[0];
        if (best) {
          const suggTitle = best.title || best.blog_title?.replace(/<[^>]*>/g, '') || '';
          if (suggTitle) setEditTitle(suggTitle);

          const platforms: string[] = Array.isArray(best.platforms) ? best.platforms : [];
          const firstPlatform = platforms[0] || best.platform || 'twitter';
          const suggContent =
            best.adaptation?.[firstPlatform] ||
            best.adaptation?.twitter ||
            best.adaptation?.linkedin ||
            best.hook ||
            best.blog_summary ||
            '';
          if (suggContent) setEditContent(suggContent);
        }
      }
    }
    // Always set title from clipTitle (course.title / clip title / episode title passed from listing)
    if (clipTitle) setEditTitle(clipTitle);
    setIsFetchingPost(false);
    const hintTimer = setTimeout(() => setShowEditHint(false), 12000);
    return () => clearTimeout(hintTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const incoming = (navState.platform || "").toLowerCase();
    if (SOCIAL_PLATFORMS.includes(incoming as SocialPlatform)) {
      setPreviewTab(incoming as Platform);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch blog suggestions ────────────────────────────────────────────────


  const fetchBlogSuggestions = useCallback(async () => {
    // Course/clip/episode suggestions come from navState.allSuggestions — not fetched via API
    if (sourceType === 'course' || sourceType === 'clip' || sourceType === 'episode') {
      return;
    }
    setIsFetchingBlogs(true);
    try {
      const email = localStorage.getItem('user_email') || '';
      // Blog suggestions API
      const { data } = await axios.get(`${BASE_URL}/api/blogs/suggestions`, {
        params: { user_email: email, post_id: postId },
        withCredentials: true,
      });
      const suggestions =
        data?.result?.data?.blog_suggestions ||
        data?.data?.blog_suggestions ||
        data?.blog_suggestions ||
        [];

      setBlogSuggestions(suggestions);
      if (suggestions.length > 0) setShowBlogSuggestions(true);
    } catch (err) {
      console.error('[PublishScheduleClip] Failed to fetch suggestions', err);
    } finally {
      setIsFetchingBlogs(false);
    }
  }, [BASE_URL, postId, sourceType]);

  const handleSelectBlogSuggestion = (suggestion: any) => {
    const platformKey = (previewTab === 'authority' ? 'linkedin' : previewTab) as keyof typeof suggestion.adaptation;

    // For courses: use course_description (full), not adaptation/hook
    // For others: use adaptation or hook
    let adaptedText = '';
    if (sourceType === 'course') {
      adaptedText = suggestion.course_description || suggestion.hook || suggestion.blog_summary || '';
    } else {
      adaptedText = suggestion.adaptation?.[platformKey] || suggestion.hook || suggestion.blog_summary || '';
    }

    // For courses: keep the existing editTitle (actual course name) — don't override with suggestion angle title
    // For blogs/clips/episodes: use suggestion title
    const rawTitle = sourceType === 'course'
      ? editTitle  // keep current title unchanged
      : (suggestion.blog_title?.replace(/<[^>]*>/g, '') || suggestion.title || '');

    // Support all URL types; ignore placeholder course_url from API
    const rawUrl = suggestion.blog_url || suggestion.clip_url || suggestion.episode_url || suggestion.course_url || '';
    const cleanUrl = rawUrl.replace(/\/\/$/, '').replace(/\/$/, '');
    const isValidUrl = cleanUrl && !cleanUrl.endsWith('podcastclip.ocesha.com') && !cleanUrl.includes('localhost');

    if (adaptedText) setEditContent(adaptedText);
    if (rawTitle) setEditTitle(rawTitle);

    // Only update URL if suggestion has a valid non-placeholder URL
    // For courses, keep the already-built ocesha.ai URL
    if (isValidUrl && (cleanUrl.includes('ocesha.ai') || cleanUrl.includes('ocesha.ai'))) {
      //setEditBlogUrl(cleanUrl);
    } else if (isValidUrl && sourceType !== 'course') {
      //setEditBlogUrl(cleanUrl);
    }

    if (suggestion.image_url) setEditThumbnailUrl(suggestion.image_url);
    setSelectedBlogId(suggestion.blog_id + '_' + (suggestion.course_id || '') + '_' + (suggestion.content_angle || ''));
    toast.success(`${sourceType === 'course' ? 'Course' : sourceType === 'clip' ? 'Clip' : sourceType === 'episode' ? 'Episode' : 'Blog'} suggestion applied!`);
  };

  /* const updatePost = async () => {
    const resolvedEmail =
      userEmail || localStorage.getItem("user_email") || "";

    const resolvedPostId =
      postId ||
      localStorage.getItem("postId") ||
      localStorage.getItem("post_id") ||
      localStorage.getItem("clipPostId") ||
      localStorage.getItem("muid") ||
      localStorage.getItem("guid") ||
      localStorage.getItem("sid") ||
      "";

    const finalContent = editBlogUrl
      ? `${editContent}\n\n${editBlogUrl}`
      : editContent;

    const platformParam = selectedPlatforms.join(" | ");

    await axios.post(
      `${BASE_URL}/api/socialmedia/api/posts/${resolvedPostId}/update` +
      `?user_email=${encodeURIComponent(resolvedEmail)}` +
      `&post_type=published`,
      {
        platform: platformParam,
        content: finalContent,
        imageUrl: editVideoUrl || null,
        url: editBlogUrl || null,
        video_url: editVideoUrl || null,
        scheduleTime: null,
        mode: 'save',
        image_urls: postImageUrls(),
      },
      {
        withCredentials: true,
      }
    );
  }; */

  const fetchPostDetails = async (postId: string) => {
    try {
      const res = await axios.post(
        `${BASE_URL}/api/socialmedia/api/posts/${postId}/post-details`,
        {
          post_id: postId,
        },
        {
          withCredentials: true,
        }
      );

      // console.log('fetching post details res =>', res.data);

      if (res.data) {
        const post = res.data.post_det;
        setFetchedPostData(post);

        // live version = the stored fields, draft = unpublished edits (if any)
        const live = snapshotFromPost(post);
        const draft =
          post?.has_draft !== false && post?.draft && typeof post.draft === "object"
            ? { ...live, ...post.draft }
            : null;
        setLiveVersion(live);
        setDraftVersion(draft);
        setVersionView("draft");
        workingCopyRef.current = null;

        if (draft) {
          applySnapshot(draft);                 // continue editing where the user left off
        } else {
          setEditTitle(live.title || clipTitle || "");
          setEditContent(live.content);
          setEditThumbnailUrl(live.image_url);
          setEditVideoUrl(live.video_url);
        }
      }
    } catch (err) {
      console.error("Failed to fetch updated post", err);
    }
  };

  const updatePost = async () => {
    try {
      setSavingPost(true);

      const resolvedEmail =
        userEmail || localStorage.getItem("user_email") || "";

      const resolvedPostId =
        postId ||
        localStorage.getItem("postId") ||
        localStorage.getItem("post_id") ||
        localStorage.getItem("clipPostId") ||
        localStorage.getItem("muid") ||
        localStorage.getItem("guid") ||
        localStorage.getItem("sid") ||
        "";

      let finalImageUrl = editThumbnailUrl;
      let finalVideoUrl = editVideoUrl;


      // Upload local image if selected
      /* if (imageFile) {
        const formData = new FormData();
        formData.append("post_id", resolvedPostId);
        if (imageFile) {
          formData.append("post_image", imageFile);
        }
        if (videoFile) {
          formData.append("post_video", videoFile);
        }
  
        const uploadResponse = await axios.post(
          `${BASE_URL}/api/socialmedia/api/posts/update_image`,
          formData,
          {
            withCredentials: true,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
  
        if (uploadResponse.data.success) {
  
          if (uploadResponse.data.image_url) {
            setEditThumbnailUrl(uploadResponse.data.image_url);
          }
  
          if (uploadResponse.data.video_url) {
            setEditVideoUrl(uploadResponse.data.video_url);
          }
        }
      } */
      if (imageFile) {
        const imageFormData = new FormData();

        imageFormData.append("post_id", resolvedPostId);
        imageFormData.append("post_image", imageFile);
        imageFormData.append("draft", "true");   // store the file only, don't change the live post

        const uploadResponse = await axios.post(
          `${BASE_URL}/api/socialmedia/api/posts/update_image`,
          imageFormData,
          {
            withCredentials: true,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (uploadResponse.data.image_url) {
          finalImageUrl = uploadResponse.data.image_url;
          setEditThumbnailUrl(finalImageUrl);
        }
      }

      let uploadedVideoUrl = editVideoUrl;

      if (videoFile) {
        const videoFormData = new FormData();

        videoFormData.append("post_id", resolvedPostId);
        videoFormData.append("video_file", videoFile);

        const videoUploadResponse = await axios.post(
          `${BASE_URL}/api/socialmedia/api/posts/${resolvedPostId}/upload-video?draft=true`,
          videoFormData,
          {
            withCredentials: true,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        // console.log('video uploaded response =>', videoUploadResponse);
        if (videoUploadResponse.data.video_url) {
          finalVideoUrl = videoUploadResponse.data.video_url.video_url;
          setEditVideoUrl(finalVideoUrl);
        }
      }

      // Save only — nothing is published here. For a live post this becomes a draft
      // (live post untouched); for a post that isn't live it updates the post itself.
      const saveRes = await axios.post(
        `${BASE_URL}/api/socialmedia/api/posts/${resolvedPostId}/save-draft` +
        `?user_email=${encodeURIComponent(resolvedEmail)}`,
        {
          title: editTitle,
          content: editContent,
          imageUrl: finalImageUrl || "",
          video_url: finalVideoUrl || "",
          url: editBlogUrl || "",
          image_urls: postImageUrls(),
        },
        { withCredentials: true }
      );

      const savedAs: "draft" | "live" = saveRes?.data?.saved_as === "draft" ? "draft" : "live";
      const snap = {
        title: (editTitle || "").trim(),
        content: editContent,
        image_url: finalImageUrl || "",
        image_urls: postImageUrls() || [],
        video_url: finalVideoUrl || "",
        url: editBlogUrl || "",
      };
      if (savedAs === "draft") {
        setDraftVersion({ ...snap, ...(saveRes?.data?.draft || {}) });
      } else {
        setLiveVersion(snap);
        setDraftVersion(null);
      }
      workingCopyRef.current = null;
      setVersionView("draft");

      return { finalImageUrl, finalVideoUrl, savedAs };
    } catch (error) {
      console.error(error);
      toast.error("Failed to update post. Please try again later!");
    } finally {
      setSavingPost(false);
    }
  };

  const handleSaveEdit = async () => {
    const result = await updatePost();

    if (!result) return;          // save failed: error already shown, keep the editor open

    setEditThumbnailUrl(result.finalImageUrl);
    setEditVideoUrl(result.finalVideoUrl);
    // await fetchPostDetails(postId);

    /* setFetchedPostData((prev: any) => ({
      ...prev,
      title: editTitle,
      content: editContent,
      imageUrl: editThumbnailUrl,
      video_url: editVideoUrl,
    })); */
    toast.success(
      result.savedAs === "draft"
        ? "Draft saved — your live post is unchanged. Publish to make these changes live."
        : "Changes saved — click Schedule to publish"
    );
    setIsEditingPreview(false);
  };

  useEffect(() => {
    const resolvedPostId =
      postId ||
      localStorage.getItem("postId") ||
      localStorage.getItem("post_id");

    if (resolvedPostId) {
      fetchPostDetails(resolvedPostId);
    }
  }, []);

  const togglePlatform = (id: Platform) => {
    if (id === "authority") {
      return; // Always selected
    }

    if (isComingSoonPlatform(id)) {
      setComingSoonPlatform(PLATFORMS.find((x) => x.id === id)?.label || id);
      setComingSoonOpen(true);
      return;
    }

    if (!connectionState[id as SocialPlatform]) {
      toast.info(`Please connect ${id} first using the buttons at the top.`);
      return;
    }

    if (planTier === "FREE") {
      setUpgradeModal({
        open: true,
        title: "Upgrade Required",
        message:
          "Social media publishing isn't included in your current plan. Upgrade to connect and publish to social platforms.",
      });
      return;
    }

    setSelectedPlatforms((prev) => {
      const isCurrentlySelected = prev.includes(id);

      // Deselecting is always allowed; Authority Page is untouched.
      if (isCurrentlySelected) {
        return prev.filter((p) => p !== id);
      }

      if (planTier === "STARTER") {
        // Only one social platform allowed at a time on Starter — swap it out.
        const withoutSocial = prev.filter((p) => p === "authority");
        return [...withoutSocial, id];
      }

      // GROWTH / PRO — allow any number of social platforms.
      return [...prev, id];
    });
  };

  const toggleAll = () => {
    if (planTier === "FREE" || planTier === "STARTER") {
      // "Select all" doesn't apply on plans limited to 0 or 1 social platform.
      return;
    }

    const allEligible: Platform[] = ["authority", ...SOCIAL_PLATFORMS.filter(p => connectionState[p] && !isComingSoonPlatform(p))];
    if (selectedPlatforms.length === allEligible.length) {
      setSelectedPlatforms(["authority"]);  // keep Authority Page selected
    } else {
      setSelectedPlatforms(allEligible);
    }
  };

  const getLatestUserDetails = async () => {
    try {
      const response = await getUserDetailsMutation({
        email: useremail || getCookie("user_email") || "",
        filter: "",
        start_date: "",
        end_date: "",
      });

      if ("data" in response) {
        setUserDets(response.data.data);
        return response.data.data;   // <-- return latest user
      }

      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const handlePublish = async () => {
    if (isViewingLive) {
      toast.info("You're viewing the live version. Switch to the Draft to publish your changes.");
      return;
    }
    const latestUser = await getLatestUserDetails();
    if (
      ["level_0a"].includes(latestUser?.subscription_level) /*&&
      Number(latestUser?.total_courses_publish) <= 0*/
    ) {
      setUpgradeModal({
        open: true,
        title: "Upgrade plan",
        message:
          "Upgrade your plan to continue publishing.",
      });

      //navigate("/pricing");
      return;
    }

    setApiError("");
    setIsLoading(true);
    try {
      const resolvedEmail = userEmail || localStorage.getItem('user_email') || '';
      const resolvedPostId = postId
        || localStorage.getItem('postId')
        || localStorage.getItem('post_id')
        || localStorage.getItem('clipPostId')
        || localStorage.getItem('muid')
        || localStorage.getItem('guid')
        || localStorage.getItem('sid')
        || '';

      if (!resolvedPostId) {
        setApiError("Post ID missing. Please go back and try again.");
        setIsLoading(false);
        return;
      }

      const hasAuthority = selectedPlatforms.includes("authority");

      // YouTube only accepts videos: without one, skip YouTube (after telling the user)
      const hasVideo = !!videoFile || /\.(mp4|webm|ogg|mov|m4v|mkv|avi)(\?|#|$)/i.test(editVideoUrl || "");
      let platformsToUse = selectedPlatforms;
      if (selectedPlatforms.includes("youtube") && !hasVideo) {
        const others = selectedPlatforms.filter((p) => p !== "youtube");
        if (others.length === 0) {
          setApiError("YouTube only accepts videos. Add a video to this post or choose another platform.");
          setIsLoading(false);
          return;
        }
        // a toast, not window.confirm: confirm dialogs can be blocked inside the SHOPLINE iframe
        toast.warning("This post has no video. YouTube only accepts videos, so YouTube will be skipped.", { autoClose: 8000 });
        platformsToUse = others;
      }

      const realPlatforms = platformsToUse.filter(
        (p) => p !== "authority"
      ) as SocialPlatform[];

      // Authority Page alone is valid — it publishes via the activate API
      if (realPlatforms.length === 0 && !hasAuthority) {
        setApiError("Please select at least one platform.");
        setIsLoading(false);
        return;
      }

      const disconnected = realPlatforms.filter(p => !connectionState[p]);
      if (disconnected.length > 0) {
        setApiError(`Not connected to: ${disconnected.join(', ')}. Please connect first.`);
        setIsLoading(false);
        return;
      }

      // Expired / invalid connections: stop here and tell the user what to do
      const issues = await checkConnections(realPlatforms as string[]);
      const problems = Object.values(issues).map((i) => i.message).filter(Boolean);
      if (problems.length > 0) {
        setApiError(problems.join(" "));
        problems.forEach((m) => toast.error(m, { autoClose: 10000 }));
        setIsLoading(false);
        return;
      }

      const emailParam = resolvedEmail
        ? `?user_email=${encodeURIComponent(resolvedEmail)}`
        : '';

      // When only Authority Page is selected, treat as authority-only publish
      const primaryPlatform = realPlatforms.length > 0 ? realPlatforms[0] : (hasAuthority ? "authority" : undefined);
      const additionalPlatforms = realPlatforms.slice(1);
      // Build final content with blog URL appended for API
      const finalContent = editBlogUrl
        ? `${editContent || navState.postContent || ''}\n\n${editBlogUrl}`
        : (editContent || navState.postContent || '');

      // STEP 0 (Course only): Generate a real social post from the course to get a valid postId
      let effectivePostId: string = resolvedPostId;
      // if (sourceType === 'course') {
      //   try {
      //     const generatePayload = {
      //       suggestion: {
      //         type: 'Social Post',
      //         title: editTitle || clipTitle || '',
      //         hook: (editContent || '').substring(0, 300),
      //         source_reference: editTitle || clipTitle || '',
      //         platform: primaryPlatform === 'authority' ? 'linkedin' : (primaryPlatform || 'linkedin'),
      //         estimated_engagement: 'high',
      //         content_angle: 'informative',
      //         key_points: [(editContent || '').substring(0, 150)].filter(Boolean),
      //         image_url: editVideoUrl || clipThumbnail || '',
      //         image_url_small: editVideoUrl || clipThumbnail || '',
      //       },
      //     };
      //     const genRes = await axios.post(
      //       `${BASE_URL}/api/prompts/api/posts/generate?user_email=${encodeURIComponent(resolvedEmail)}`,
      //       generatePayload,
      //       { headers: { 'Content-Type': 'application/json', accept: 'application/json' } }
      //     );
      //     if (genRes.data?.status === 'success' && genRes.data?.posts?.[0]?.id) {
      //       effectivePostId = genRes.data.posts[0].id;
      //     }
      //   } catch (genErr: any) {
      //     console.warn('[PublishScheduleClip] Course post generate failed:', genErr?.response?.data?.detail || genErr?.message);
      //   }
      // }

      // STEP 1: Patch the post platform/content
      // ── SINGLE UPDATE CALL — scheduler handles actual posting ──
      // Rules:
      //  • authority only, Publish Now    → publish immediately, no schedule time
      //  • authority only, Schedule Later → real schedule time (now supported)
      //  • authority + others (now)       → schedule ~30s ahead so scheduler posts socials
      //  • authority + others (later)     → real schedule time
      //  • others only                    → publish (~30s ahead) or real schedule time
      const postType = timing === "later" ? "scheduled" : "published";

      // Full platform selection as a pipe-joined string,
      // e.g. "authority | linkedin | twitter"
      const platformParam = platformsToUse.join(" | ");

      const isAuthorityOnlySelection = hasAuthority && realPlatforms.length === 0;

      // Decide the schedule_time to send to /update
      let scheduleTimeISO: string | null = null;

      if (timing === "later") {
        // Real future schedule time chosen by the user — applies to any
        // platform combination, including Authority-Page-only.
        const parsed = new Date(`${scheduleDate} ${scheduleTime}`);
        scheduleTimeISO = isNaN(parsed.getTime())
          ? `${scheduleDate} ${scheduleTime}`
          : parsed.toISOString();
      } else if (isAuthorityOnlySelection) {
        // Authority only + Publish Now → immediate, no schedule time
        scheduleTimeISO = null;
      } else {
        // Publish now, but socials must go through the scheduler →
        // set ~30s in the future so the background job picks it up promptly
        scheduleTimeISO = new Date(Date.now() + 30 * 1000).toISOString();
      }

      try {
        const response = await axios.post(
          `${BASE_URL}/api/socialmedia/api/posts/${effectivePostId}/update` +
          `?user_email=${encodeURIComponent(resolvedEmail)}` +
          `&post_type=${encodeURIComponent(postType)}`,
          {
            platform: platformParam,
            content: finalContent,
            scheduleTime: scheduleTimeISO,          // null only for authority-only
            imageUrl: editThumbnailUrl || null,
            url: editBlogUrl || null,
            video_url: editVideoUrl || null,
            image_urls: postImageUrls(),
          },
          { withCredentials: true }
        );

        if (response.data?.status === "error") {
          setApiError(response.data?.message || "Failed. Please try again.");
          setIsLoading(false);
          return;
        }
      } catch (err: any) {
        const detail =
          err?.response?.data?.detail || err?.message || "Update failed. Please try again.";
        setApiError(typeof detail === "string" ? detail : "Update failed. Please try again.");
        setIsLoading(false);
        return;
      }

      // STEP 2: schedule or publish primary
      // if (timing === "later") {
      //   const parsedDate = new Date(`${scheduleDate} ${scheduleTime}`);
      //   const scheduleTimeISO = isNaN(parsedDate.getTime())
      //     ? `${scheduleDate} ${scheduleTime}`
      //     : parsedDate.toISOString();

      //   const response = await axios.post(
      //     `${BASE_URL}/api/prompts/api/posts/${effectivePostId}/schedule${emailParam}`,
      //     { schedule_time: scheduleTimeISO }
      //   );

      //   if (response.data?.status === "error") {
      //     setApiError(response.data?.message || "Schedule failed. Please try again.");
      //     setIsLoading(false);
      //     return;
      //   }

      //   if (additionalPlatforms.length > 0) {
      //     await Promise.all(
      //       additionalPlatforms.map((platform) =>
      //         axios.post(`${BASE_URL}/api/socialmedia/api/schedule-post`, {
      //           content: finalContent,
      //           platform,
      //           scheduleTime: scheduleTimeISO,
      //           imageUrl: editVideoUrl || null,
      //         }).catch((e) => {
      //           console.warn(
      //             `[PublishScheduleClip] Failed to schedule on ${platform}:`,
      //             e?.response?.data?.detail || e?.message
      //           );
      //         })
      //       )
      //     );
      //   }
      // } else {
      //   // Publish NOW
      //   if (primaryPlatform === "twitter") {
      //     const response = await axios.post(
      //       `${BASE_URL}/api/socialmedia/api/posts/${effectivePostId}/publish-twitter`
      //     );
      //     if (response.data?.status === "error") {
      //       setApiError(response.data?.message || "Publish failed. Please try again.");
      //       setIsLoading(false);
      //       return;
      //     }
      //   } else {
      //     const response = await axios.post(
      //       `${BASE_URL}/api/prompts/api/posts/${effectivePostId}/activate${emailParam}`
      //     );
      //     if (response.data?.status === "error") {
      //       setApiError(response.data?.message || "Publish failed. Please try again.");
      //       setIsLoading(false);
      //       return;
      //     }
      //   }

      //   if (additionalPlatforms.length > 0) {
      //     await Promise.all(
      //       additionalPlatforms.map(async (platform) => {
      //         try {
      //           if (platform === "twitter") {
      //             return await axios.post(
      //               `${BASE_URL}/api/socialmedia/api/post/twitter`,
      //               { content: finalContent, imageUrl: editVideoUrl || null }
      //             );
      //           }
      //           return await axios.post(
      //             `${BASE_URL}/api/socialmedia/api/schedule-post`,
      //             {
      //               content: finalContent,
      //               platform,
      //               scheduleTime: new Date().toISOString(),
      //               imageUrl: editVideoUrl || null,
      //             }
      //           );
      //         } catch (e: any) {
      //           console.warn(
      //             `[PublishScheduleClip] Failed to publish on ${platform}:`,
      //             e?.response?.data?.detail || e?.message
      //           );
      //         }
      //       })
      //     );
      //   }
      // }

      onPublish?.({
        platforms: platformsToUse,
        timing,
        scheduleDate: timing === "later" ? scheduleDate : null,
        scheduleTime: timing === "later" ? scheduleTime : null,
        customizePerPlatform,
        addFollowup,
        followupType,
        goal,
        toneOfVoice,
        keyMessage,
      });

      const platformNames = realPlatforms.join(', ');
      const successMsg = timing === 'later'
        ? `Post scheduled ${platformNames}`
        : `Post published ${platformNames}`;

      toast.success(successMsg);

      // For course source type: fetch task details to build the course URL
      // if (sourceType === 'course') {
      //   setTimeout(async () => {
      //     try {
      //       const email = localStorage.getItem('user_email') || '';
      //       const taskRes = await axios.get(
      //         `${BASE_URL}/api/prompts/task_status`,
      //         { params: { task_id: effectivePostId, user_email: email }, withCredentials: true }
      //       );
      //       const taskData = taskRes.data?.result?.data || taskRes.data?.data || taskRes.data || {};
      //       const profileId =
      //         taskData.profile_id ||
      //         taskData.user_id ||
      //         localStorage.getItem('profile_id') ||
      //         localStorage.getItem('user_id') ||
      //         email;
      //       // Build slug from course title: lowercase, spaces to hyphens
      //       const rawCourseTitle =
      //         taskData.course_title ||
      //         taskData.title ||
      //         editTitle ||
      //         clipTitle ||
      //         '';
      //       const titleSlug = rawCourseTitle
      //         .toLowerCase()
      //         .replace(/[^a-z0-9\s-]/g, '')
      //         .trim()
      //         .replace(/\s+/g, '-');
      //       const courseUrl = `https://ocesha.ai/${encodeURIComponent(profileId)}/course/${titleSlug}`;
      //       window.location.href = courseUrl;
      //     } catch (e) {
      //       navigate(-1);
      //     }
      //   }, 2000);
      // } else {
      //   setTimeout(() => navigate(-1), 2500);
      // }

    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || "Network error. Please try again.";
      setApiError(typeof detail === "string" ? detail : "Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const getPlatformDescription = () => {
    const descriptions: Record<string, string> = {
      linkedin: fetchedPostData?.linkedin_description || editContent || fetchedPostData?.content || "",
      twitter: fetchedPostData?.twitter_description || editContent || fetchedPostData?.content || "",
      facebook: fetchedPostData?.facebook_description || editContent || fetchedPostData?.content || "",
      youtube: fetchedPostData?.youtube_description || editContent || fetchedPostData?.content || "",
      authority: fetchedPostData?.authority_description || editContent || fetchedPostData?.content || "",
    };
    return descriptions[previewTab] || "";
  };

  const getPlatformTitle = () => {
    const titles: Record<string, string> = {
      linkedin: fetchedPostData?.linkedin_title || editTitle || fetchedPostData?.title || "",
      twitter: fetchedPostData?.twitter_title || editTitle || fetchedPostData?.title || "",
      facebook: fetchedPostData?.facebook_title || editTitle || fetchedPostData?.title || "",
      youtube: fetchedPostData?.youtube_title || editTitle || fetchedPostData?.title || "",
      authority: fetchedPostData?.authority_title || editTitle || fetchedPostData?.title || "",
    };
    return titles[previewTab] || "";
  };

  const displayTitle = getPlatformTitle();
  const displayContent = editContent || fetchedPostData?.content || navState.postContent || '';

  const getRawVideoUrl = () => (editVideoUrl ? getFullVideoUrl(editVideoUrl) : (editThumbnailUrl || clipThumbnail || ''));
  const displayVideoUrl = (() => {
    const raw = getRawVideoUrl();
    if (previewTab === "youtube") {
      const embedded = toYouTubeEmbed(raw);
      return embedded || raw;
    }
    return raw;
  })();

  // Add this after the toYouTubeEmbed function or around line 500-520
  const getPublishHeaderText = () => {
    switch (sourceType) {
      case 'blog':
        return 'Publish or Schedule Blog';
      case 'image':
        return 'Publish or Schedule Image';
      case 'course':
        return 'Publish or Schedule Course';
      case 'social':
      case 'socialpost':
        return 'Publish or Schedule Social post';
      case 'episode':
        return 'Publish or Schedule Episode';
      case 'clip':
      default:
        return 'Publish or Schedule Clip';
    }
  };

  const getPublishSubHeaderText = () => {
    switch (sourceType) {
      case 'blog':
        return 'Choose where and when you want to share your blog.';
      case 'image':
        return 'Choose where and when you want to share your image.';
      case 'course':
        return 'Choose where and when you want to share your course.';
      case 'social':
      case 'socialpost':
        return 'Choose where and when you want to share your social post.';
      case 'episode':
        return 'Choose where and when you want to share your episode.';
      case 'clip':
      default:
        return 'Choose where and when you want to share your clip.';
    }
  };

  // ─── Sidebar state ────────────────────────────────────────────────────────
  const [activeItem, setActiveItem] = useState<string>(localStorage.getItem("activeItem") || "clips");
  const [contentLibOpen, setContentLibOpen] = useState<boolean>(true);
  const [openAIConcierge, setOpenAIConcierge] = useState<boolean>(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const isProfileMenuOpen = Boolean(profileMenuAnchor);

  const handleSidebarNav = (itemName: string) => {
    setActiveItem(itemName);

    if (itemName === "logout") {
      localStorage.setItem("logged_in", "false");
      localStorage.clear();
      window.location.href = process.env.REACT_APP_LOGOUT_DOMAIN || ''; //"https://ocesha.ai";
      return;
    }

    if (itemName === "upgrade") {
      localStorage.setItem("fromUpgrade", "true");
      navigate("/pricing", { state: { from_page: "blog_listing" } });
      return;
    }

    if (itemName === "course" || itemName === "mycourse") {
      localStorage.setItem("activeItem", "course");
      navigate("/codegenie/generated-courses");
      return;
    }

    if (itemName === "createcourse") {
      localStorage.setItem("activeItem", "createcourse");
      navigate("/codegenie/new-course-2-1");
      return;
    }

    if (itemName === "profile" /* || itemName === "authority" */) {
      localStorage.setItem('activeItem', 'profile');
      navigate("/codegenie/generated-blogs", {
        replace: true,
        state: { creator: userDets },
      });
      return;
    }

    // All panels live inside ocesha_new_design at /codegenie/generated-blogs
    // including authority (Dashboard) and payment (Connect Stripe)
    const panelMap: Record<string, string> = {
      authority: "authority",
      dashboard: "authority",
      blogs: "blogs",
      episodes: "episodes",
      clips: "clips",
      images: "images",
      socailpost: "socailpost",
      social: "socailpost",
      digital: "digital",
      digitaltwin: "digital",
      messages: "message",
      message: "message",
      profile: "profile",
      analytics: "analytics",
      settings: "settings",
      billing: "billing",
      payment: "payment",
      shopline: "shopline",
    };

    const panelKey = panelMap[itemName] ?? itemName;
    localStorage.setItem("activeItem", panelKey);
    navigate("/codegenie/generated-blogs", { state: { activeItem: panelKey } });
  };

  // NOTE: "Schedule for Later" is now allowed for Authority-Page-only
  // selections too — we no longer gate the UI on an authority-only check.

  const handleImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const allowedFormats = ["image/png", "image/jpeg", "image/webp", "image/gif"];

      if (!allowedFormats.includes(file.type)) {
        toast.error("Only PNG, JPG, WEBP, and GIF formats are allowed.");
        return;
      }

      setImageFile(file);

      const objectUrl = URL.createObjectURL(file);
      setEditThumbnailUrl(objectUrl);
      setChoosingImageModal(false);
    }
  };

  const handleVideoChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {

    if (!e.target.files?.length) return;

    const file = e.target.files[0];

    setVideoFile(file);

    const preview = URL.createObjectURL(file);

    setEditVideoUrl(preview);
  };

  const handleSearch = async () => {
    setUnsplashSearch(true);
    if (!query) {
      toast.error('Please enter any value to search');
    } else {
      try {
        const response = await axios.get(
          "https://api.unsplash.com/search/photos",
          {
            params: {
              query,
              per_page: 20
            },
            headers: {
              Authorization: `Client-ID ${process.env.REACT_APP_UNSPLASH_ACCESS_KEY}`
            }
          }
        );
        setImages(response.data.results);
      } catch (error) {
        console.error("Error fetching images from Unsplash", error);
      }
    }
  };

  const horizontalImages = images.filter(image => image.width > image.height);

  const handleSelectUnsplashImage = async (imageUrl: any, imageid: any) => {
    setSelectedUnsplashImg(imageid);
    // Update preview immediately
    setEditThumbnailUrl(imageUrl);

    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const file = new File([blob], "unsplash_image.jpg", { type: blob.type });

    // setImageFile(file);
  };

  const modifyImageUrl = (url: any) => {
    let modifiedUrl = new URL(url);

    modifiedUrl.searchParams.set('fit', 'none');

    modifiedUrl.searchParams.set('w', '1152');

    modifiedUrl.searchParams.set('h', '500');

    return modifiedUrl.toString();
  };

  const handleUnsplashselectionChange = () => {
    if (!selectedUnsplashImg/* editVideoUrl */) {
      toast.error('Please select any image!');
    } else {
      setOpenUnsplash(false);
      setChoosingImageModal(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  const tomorrow = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  })();

  // Prefill the date/time inputs with the incoming scheduled time when one
  // was passed in and it's not in the past; otherwise fall back to tomorrow/10:00.
  const defaultScheduleDateInput = incomingSchedule.isoDate || tomorrow;
  const defaultScheduleTimeInput = incomingSchedule.time24 || "10:00";

  // Browser's local timezone abbreviation (e.g. "IST", "PST") for the
  // currently selected schedule date/time, shown next to "Scheduled for...".
  const scheduleTimezoneAbbr = (() => {
    try {
      const parsed = new Date(`${scheduleDate} ${scheduleTime}`);
      const ref = isNaN(parsed.getTime()) ? new Date() : parsed;
      const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(ref);
      return parts.find((p) => p.type === "timeZoneName")?.value || "";
    } catch {
      return "";
    }
  })();

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <div style={S.page}>
        <style>{`
        @keyframes bounceLeft {
          0%, 100% { transform: translateX(0); }
          30% { transform: translateX(-6px); }
          60% { transform: translateX(-3px); }
        }
        @keyframes fadeInHint {
          0% { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes editBtnPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(75,165,235,0.4); }
          50% { box-shadow: 0 0 0 5px rgba(75,165,235,0); }
        }
      `}</style>

        {/* ── SIDEBAR (new ocesha_new_design style) ────────────────────── */}
        <div style={S.sidebar}>

          {/* Logo row */}
          <div style={{ padding: "18px 20px 14px 20px", display: "flex", alignItems: "center", gap: 8 }}>
            <a href={process.env.REACT_APP_LOGOUT_DOMAIN || "https://ocesha.ai"} style={{ display: "inline-flex", alignItems: "center", flex: 1, minWidth: 0 }}>
              <img src={ocesha_logo} alt="Ocesha" style={{ height: 32, width: "auto", maxWidth: "100%", objectFit: "contain", display: "block" }} />
            </a>
          </div>

          {/* AI Digital Twin card */}
          <Box sx={{
            mx: 2, mb: 2, p: 1.5, borderRadius: "12px",
            background: "linear-gradient(180deg, rgba(17,24,39,0.96) 0%, rgba(8,12,20,0.96) 100%)",
            border: "1px solid rgba(255,255,255,0.06)",
            cursor: "pointer",
          }}
            onClick={() => { setOpenAIConcierge(true); }}
          >
            <Typography sx={{
              fontSize: 10, color: "rgba(255,255,255,0.45)",
              letterSpacing: 1, fontWeight: 600, mb: 1,
            }}>
              AI Concierge
            </Typography>
            <Stack direction="row" spacing={1.2} alignItems="center" mb={1.5}>
              <Box component="img" src={RohanImg} sx={{
                width: 36, height: 36, borderRadius: "50%", objectFit: "cover",
              }} />
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>
                  AI Concierge Online
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#22C55E" }} />
                  <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
                    Ask AI Concierge
                  </Typography>
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" alignItems="center"
              sx={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: "8px", px: 1.2, py: 0.6,
                cursor: "pointer",
                "&:hover": { background: "rgba(255,255,255,0.07)" },
              }}>
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)", flex: 1 }}>
                Ask My AI Twin
              </Typography>
              <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.5)" }} />
            </Stack>
          </Box>

          {/* Main nav — matches ocesha_new_design renderSidebar() */}
          <div style={{ flex: 1, padding: "4px 0 8px 0" }}>

            {/* Dashboard */}
            {(["authority"] as const).map((id) => {
              const active = activeItem === id || activeItem === "dashboard";
              return (
                <div
                  key={id}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 20px", margin: "1px 10px", borderRadius: 8,
                    fontSize: 13.5, fontWeight: active ? 600 : 500,
                    color: active ? "#fff" : "rgba(255,255,255,0.7)",
                    background: active ? "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)" : "transparent",
                    cursor: "pointer", transition: "all 0.15s", userSelect: "none" as const,
                  }}
                  onClick={() => handleSidebarNav(id)}
                  onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.04)"; }}
                  onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = active ? "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)" : "transparent"; }}
                >
                  <AutoGraphOutlinedIcon sx={{ fontSize: 18 }} />
                  Dashboard
                </div>
              );
            })}

            {/* Profile Page */}
            {(() => {
              const active = activeItem === "profile";
              return (
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 20px", margin: "1px 10px", borderRadius: 8,
                    fontSize: 13.5, fontWeight: active ? 600 : 500,
                    color: active ? "#fff" : "rgba(255,255,255,0.7)",
                    background: active ? "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)" : "transparent",
                    cursor: "pointer", transition: "all 0.15s", userSelect: "none" as const,
                  }}
                  onClick={() => handleSidebarNav("profile")}
                  onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.04)"; }}
                  onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = active ? "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)" : "transparent"; }}
                >
                  <PersonOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                  Profile Page
                </div>
              );
            })()}

            {/* My Course */}
            {(() => {
              const active = activeItem === "course" || activeItem === "mycourse";
              return (
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 20px", margin: "1px 10px", borderRadius: 8,
                    fontSize: 13.5, fontWeight: active ? 600 : 500,
                    color: active ? "#fff" : "rgba(255,255,255,0.7)",
                    background: active ? "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)" : "transparent",
                    cursor: "pointer", transition: "all 0.15s", userSelect: "none" as const,
                  }}
                  onClick={() => handleSidebarNav("course")}
                  onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.04)"; }}
                  onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = active ? "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)" : "transparent"; }}
                >
                  <SchoolOutlinedIcon sx={{ fontSize: 18 }} />
                  My Course
                </div>
              );
            })()}

            {/* Content Library — expandable */}
            <div>
              <div
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "9px 20px", margin: "1px 10px",
                  fontSize: 13.5, fontWeight: 500, color: "rgba(255,255,255,0.7)",
                  borderRadius: 8, cursor: "pointer", transition: "all 0.15s",
                }}
                onClick={() => setContentLibOpen((v) => !v)}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.04)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <FolderOutlinedIcon sx={{ fontSize: 18 }} />
                  <span>Content Library</span>
                </div>
                {contentLibOpen ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
              </div>

              {contentLibOpen && (
                <div style={{ marginLeft: 28, marginTop: 2, marginBottom: 4 }}>
                  {([
                    { id: "blogs", label: "Blogs", icon: <ArticleOutlinedIcon sx={{ fontSize: 15 }} /> },
                    { id: "episodes", label: "Episodes", icon: <GraphicEqIcon sx={{ fontSize: 15 }} /> },
                    { id: "clips", label: "Clips", icon: <PlayCircleOutlinedIcon sx={{ fontSize: 15 }} /> },
                    { id: "images", label: "Images", icon: <ImageOutlinedIcon sx={{ fontSize: 15 }} /> },
                    { id: "socailpost", label: "Social Post", icon: <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 15 }} /> },
                  ] as { id: string; label: string; icon: React.ReactNode }[]).map((sub) => {
                    const active = activeItem === sub.id;
                    return (
                      <div
                        key={sub.id}
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "7px 12px", margin: "1px 6px",
                          fontSize: 12.5, fontWeight: 500,
                          color: active ? "#4BA5EB" : "rgba(255,255,255,0.6)",
                          background: active ? "rgba(75,165,235,0.08)" : "transparent",
                          borderRadius: 7, cursor: "pointer", transition: "all 0.15s",
                          userSelect: "none" as const,
                        }}
                        onClick={() => handleSidebarNav(sub.id)}
                        onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.03)"; }}
                        onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                      >
                        {sub.icon}
                        {sub.label}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* My Digital Twin */}
            {(() => {
              const active = activeItem === "digital";
              return (
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 20px", margin: "4px 10px 1px", borderRadius: 8,
                    fontSize: 13.5, fontWeight: 500,
                    color: active ? "#4BA5EB" : "rgba(255,255,255,0.7)",
                    background: active ? "rgba(75,165,235,0.08)" : "transparent",
                    cursor: "pointer", transition: "all 0.15s", userSelect: "none" as const,
                  }}
                  onClick={() => handleSidebarNav("digital")}
                  onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.04)"; }}
                  onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
                  My Digital Twin
                </div>
              );
            })()}

            <hr style={S.sidebarDivider} />

            {/* Bottom nav items — matches ocesha_new_design bottom nav */}
            {([
              { id: "upgrade", label: "Upgrade", icon: <TrendingUpOutlinedIcon sx={{ fontSize: 18 }} /> },
              { id: "payment", label: "Connect Stripe", icon: <PaymentOutlinedIcon sx={{ fontSize: 18 }} /> },
              // { id: "shopline", label: "Connect Shopline", icon: <StorefrontOutlinedIcon sx={{ fontSize: 18 }} /> },
              { id: "messages", label: "Messages & Leads", icon: <PeopleOutlineOutlinedIcon sx={{ fontSize: 18 }} /> },
              { id: "analytics", label: "Analytics", icon: <BarChartOutlinedIcon sx={{ fontSize: 18 }} /> },
              { id: "logout", label: "Logout", icon: <LogoutOutlinedIcon sx={{ fontSize: 18 }} /> },
            ] as { id: string; label: string; icon: React.ReactNode }[]).map((item) => {
              const active = activeItem === item.id;
              return (
                <div
                  key={item.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 20px", margin: "1px 10px",
                    fontSize: 13, fontWeight: 500,
                    color: active ? "#4BA5EB" : "rgba(255,255,255,0.65)",
                    background: active ? "rgba(75,165,235,0.08)" : "transparent",
                    borderRadius: 8, cursor: "pointer", transition: "all 0.15s",
                    userSelect: "none" as const,
                  }}
                  onClick={() => handleSidebarNav(item.id)}
                  onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.04)"; }}
                  onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  {item.icon}
                  {item.label}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── MAIN CONTENT ─────────────────────────────────────────────── */}
        <div style={S.mainContent}>

          <div style={S.topBar}>
            <button style={S.backBtn} onClick={handleBack}>
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              Go Back
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {/* Bell notification */}
              <IconButton sx={{ position: "relative", color: "rgba(255,255,255,0.7)" }}>
                <NotificationsNoneOutlinedIcon sx={{ fontSize: 22 }} />
                <Box sx={{
                  position: "absolute", top: 6, right: 6,
                  width: 18, height: 18, borderRadius: "50%",
                  background: "#EF4444", color: "#fff",
                  fontSize: 10, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  0
                </Box>
              </IconButton>

              {/* Avatar — click to open profile dropdown */}
              <Box
                component="img"
                src={userDets?.user_image || UserImg}
                onClick={(e: any) => setProfileMenuAnchor(e.currentTarget)}
                sx={{
                  width: 36, height: 36, borderRadius: "50%", objectFit: "cover",
                  border: "2px solid rgba(255,255,255,0.1)", cursor: "pointer",
                }}
              />

              {/* Profile dropdown menu — same as ocesha_new_design */}
              <Menu
                anchorEl={profileMenuAnchor}
                open={isProfileMenuOpen}
                onClose={() => setProfileMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    background: "#0F1117",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "10px",
                    minWidth: 200,
                    color: "#fff",
                    "& .MuiMenuItem-root": {
                      fontSize: 13,
                      py: 1, px: 1.5,
                      "&:hover": { background: "rgba(255,255,255,0.06)" },
                    },
                    "& .MuiListItemIcon-root": {
                      minWidth: 32, color: "rgba(255,255,255,0.65)",
                    },
                  },
                }}
              >
                {/* User info header */}
                <Box sx={{ px: 1.5, py: 1.2, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>
                    {resolvedUserName}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.5)", mt: 0.2 }}>
                    {userEmail || localStorage.getItem("user_email") || ""}
                  </Typography>
                </Box>

                <MenuItem onClick={() => {
                  setProfileMenuAnchor(null);
                  handleSidebarNav("profile");
                }}>
                  <ListItemIcon><PersonOutlineOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                  Profile
                </MenuItem>
                <MenuItem onClick={() => {
                  setProfileMenuAnchor(null);
                  handleSidebarNav("settings");
                }}>
                  <ListItemIcon><SettingsOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                  Settings
                </MenuItem>
                <MenuItem onClick={() => {
                  setProfileMenuAnchor(null);
                  handleSidebarNav("billing");
                }}>
                  <ListItemIcon><CreditCardOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                  Billing
                </MenuItem>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />
                <MenuItem
                  onClick={() => { setProfileMenuAnchor(null); handleSidebarNav("logout"); }}
                  sx={{ color: "#F87171 !important" }}
                >
                  <ListItemIcon sx={{ "& svg": { color: "#F87171" } }}>
                    <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </div>
          </div>

          {/* ── CONNECTIONS BAR ─────────────────────────────────── */}
          <div style={S.connectionsBar}>
            <span style={S.connectionsLabel}>Connections:</span>
            {CONNECTABLE_PLATFORMS.map((p) => {
              const isConnected = connectionState[p.id];
              const isConnecting = connectingPlatform === p.id;
              const isHovered = hoveredChip === p.id;
              const details = connectionDetails[p.id];
              const handle = details?.screen_name || details?.page_name || details?.urn;

              return (
                <Tooltip
                  key={p.id}
                  title={
                    isConnected
                      ? `${p.label}${handle ? ` (${handle})` : ""} — click to disconnect`
                      : `Connect ${p.label} to enable posting`
                  }
                >
                  <div
                    style={S.connectChip(isConnected, p.brandColor, isHovered)}
                    onClick={() => {
                      if (isConnecting) return;

                      // LinkedIn and YouTube can connect; Twitter / Facebook → Coming Soon
                      if (isComingSoonPlatform(p.id)) {
                        setComingSoonPlatform(p.label);
                        setComingSoonOpen(true);
                        return;
                      }

                      if (isConnected) handleDisconnect(p.id);
                      else handleConnect(p.id);
                    }}
                    onMouseEnter={() => setHoveredChip(p.id)}
                    onMouseLeave={() => setHoveredChip(null)}
                  >
                    {isConnecting ? (
                      <CircularProgress size={16} sx={{ color: p.brandColor, flexShrink: 0 }} />
                    ) : isConnected ? (
                      <CheckCircleIcon
                        sx={{
                          fontSize: 18,
                          color: isHovered ? "#ef4444" : "#22C55E",
                          transition: "color 0.18s ease",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <AddCircleOutlineIcon
                        sx={{ fontSize: 18, color: "rgba(255,255,255,0.5)", flexShrink: 0 }}
                      />
                    )}

                    <span style={{ color: p.brandColor, display: "flex", alignItems: "center", flexShrink: 0 }}>
                      {p.icon}
                    </span>

                    <span>{p.shortLabel}</span>

                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        color: isConnecting
                          ? "rgba(255,255,255,0.55)"
                          : isConnected
                            ? isHovered ? "#ef4444" : "#22C55E"
                            : "rgba(255,255,255,0.5)",
                        transition: "color 0.18s ease",
                        textTransform: "uppercase" as const,
                      }}
                    >
                      {isConnecting
                        ? "Connecting…"
                        : isConnected
                          ? isHovered ? "Disconnect" : "Connected"
                          : "Connect"}
                    </span>
                  </div>
                </Tooltip>
              );
            })}
          </div>

          <div style={S.body}>
            {/* ── LEFT PANEL ── */}
            <div style={S.leftPanel}>

              <div style={S.pageHeader}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 12,
                      background: "rgba(75,165,235,0.08)",
                      border: "1px solid rgba(75,165,235,0.2)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <SendOutlinedIcon sx={{ fontSize: 20, color: "#4BA5EB" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#E8EAF0" }}>
                        {getPublishHeaderText()}
                      </div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
                        {getPublishSubHeaderText()}
                      </div>
                    </div>
                  </div>
                </div>
                <button style={S.closeBtn} onClick={handleClose}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </button>
              </div>

              <div>
                <div style={S.sectionLabel}>1. Select Platforms</div>
                <div style={S.sectionSub}>
                  Choose one or more platforms to publish to. Connect a platform above before selecting it.
                </div>

                {PLATFORMS.map((platform) => {
                  const isAuthority = platform.id === "authority";
                  const isConnected = isAuthority || connectionState[platform.id as SocialPlatform];
                  const isSelected = selectedPlatforms.includes(platform.id);
                  const isComingSoon = !isAuthority && isComingSoonPlatform(platform.id);
                  const isPlanRestricted = !isAuthority && !isComingSoon && planTier === "FREE";
                  const isDisabled = isComingSoon || !isConnected || isPlanRestricted;
                  const isReadOnly = platform.readOnly ?? false;

                  return (
                    <div
                      key={platform.id}
                      style={S.platformCard(isSelected, isAuthority, isDisabled)}
                      onClick={() => {
                        if (!isReadOnly) {
                          togglePlatform(platform.id);
                        }
                      }}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: 8,
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.07)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {platform.icon}
                      </div>

                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={S.platformLabel}>{platform.label}</span>
                          {isAuthority && <span style={S.automaticBadge}>Required</span>}
                          {isComingSoon && (
                            <span style={S.notConnectedBadge}>Coming soon</span>
                          )}
                          {!isAuthority && isPlanRestricted && (
                            <span style={S.notConnectedBadge}>Upgrade to unlock</span>
                          )}
                          {!isAuthority && !isComingSoon && !isPlanRestricted && !isConnected && (
                            <span style={S.notConnectedBadge}>Not connected</span>
                          )}
                          {!isAuthority && !isComingSoon && isConnected && connectionIssues[platform.id] && (
                            <span style={{ ...S.notConnectedBadge, color: "#F87171", background: "rgba(248,113,113,0.12)", border: "1px solid rgba(248,113,113,0.35)" }}>
                              {connectionIssues[platform.id].status === "expired" ? "Connection expired" : "Needs attention"}
                            </span>
                          )}
                        </div>
                        {!isAuthority && !isComingSoon && isConnected && connectionIssues[platform.id] && (
                          <div style={{ fontSize: 11, color: "#F87171", marginTop: 2 }}>
                            {connectionIssues[platform.id].message}{" "}
                            <span
                              style={{ color: "#4BA5EB", cursor: "pointer", textDecoration: "underline" }}
                              onClick={(e) => { e.stopPropagation(); handleConnect(platform.id as SocialPlatform); }}
                            >
                              Reconnect →
                            </span>
                          </div>
                        )}
                        {platform.handle && isConnected && (
                          <div style={S.platformHandle}>{platform.handle}</div>
                        )}
                        {!isAuthority && !isConnected && (
                          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>
                            <span
                              style={{ color: "#4BA5EB", cursor: "pointer", textDecoration: "underline" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const socialPlatform = platform.id as SocialPlatform;

                                // Twitter / Facebook -> coming soon popup; the others start the connect flow
                                if (!isComingSoonPlatform(socialPlatform)) {
                                  handleConnect(socialPlatform);
                                  return;
                                }

                                setComingSoonPlatform(platform.label);
                                setComingSoonOpen(true);
                              }}
                            >
                              Connect {platform.label} →
                            </span>
                          </div>
                        )}
                        {isAuthority && (
                          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>
                            This will be published to your public profile page.{" "}
                            <span
                              style={{ color: "#4BA5EB", cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const profileId =
                                  localStorage.getItem('profile_id') ||
                                  localStorage.getItem('user_id') ||
                                  localStorage.getItem('user_email') ||
                                  '';
                                const authorityUrl = profileId
                                  ? `${window.location.origin}/profile/${encodeURIComponent(profileId)}`
                                  : `${window.location.origin}/profile`;
                                window.open(authorityUrl, '_blank');
                              }}
                            >
                              View Profile ↗
                            </span>
                          </div>
                        )}
                      </div>

                      <Checkbox
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => {
                          if (!isReadOnly) {
                            togglePlatform(platform.id);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          color: "rgba(255,255,255,0.25)",
                          "&.Mui-checked": { color: "#4BA5EB" },
                          "&.Mui-disabled": { color: "rgba(255,255,255,0.15)" },
                          padding: 0,
                        }}
                      />
                    </div>
                  );
                })}

                <div style={S.selectAllRow}>
                  {planTier === "GROWTH" || planTier === "PRO" ? (
                    <span
                      style={{ fontSize: 12, color: "#4BA5EB", cursor: "pointer", fontWeight: 600 }}
                      onClick={toggleAll}
                    >
                      {(() => {
                        const eligibleCount = 1 + SOCIAL_PLATFORMS.filter(p => connectionState[p]).length;
                        return selectedPlatforms.length === eligibleCount ? "Deselect All" : "Select All Connected";
                      })()}
                    </span>
                  ) : (
                    <span />
                  )}
                  <span style={{ fontSize: 12, color: "#4BA5EB", fontWeight: 600 }}>
                    {selectedPlatforms.length} selected
                  </span>
                </div>
              </div>

              <div>
                <div style={S.sectionLabel}>2. Choose Timing</div>
                <div style={S.sectionSub}>Publish now or schedule for later.</div>

                <div style={S.timingCard(timing === "now")} onClick={() => setTiming("now")}>
                  <div style={{
                    width: 18, height: 18, borderRadius: "50%",
                    border: `2px solid ${timing === "now" ? "#4BA5EB" : "rgba(0,0,0,0.15)"}`,
                    background: timing === "now" ? "#4BA5EB" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, marginTop: 1,
                  }}>
                    {timing === "now" && (<div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />)}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#E8EAF0" }}>Publish Now</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>
                      Your clip will be published immediately.
                    </div>
                  </div>
                </div>

                <div style={S.timingCard(timing === "later")}
                  onClick={() => setTiming("later")}
                >
                  <div style={{
                    width: 18, height: 18, borderRadius: "50%",
                    border: `2px solid ${timing === "later" ? "#4BA5EB" : "rgba(0,0,0,0.15)"}`,
                    background: timing === "later" ? "#4BA5EB" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, marginTop: 1,
                  }}>
                    {timing === "later" && (<div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#E8EAF0" }}>Schedule for Later</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>
                      Choose a date and time to publish.
                    </div>

                    {timing === "later" && (
                      <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12 }}>
                        <style>{`
                        .sched-date-input::-webkit-calendar-picker-indicator { opacity: 0; width: 100%; height: 100%; position: absolute; right: 0; pointer; }
                        .sched-date-input { position: relative; color-scheme: dark; cursor: pointer; }
                      `}</style>
                        <div style={S.dateTimeRow}>
                          {/* Date picker — direct visible input */}
                          <div style={{ ...S.dateInput, padding: 0, overflow: "hidden", cursor: 'default' }}>
                            <CalendarTodayIcon sx={{ fontSize: 14, color: "#4BA5EB", flexShrink: 0, marginLeft: 10, cursor: 'default' }} />
                            <input
                              className="sched-date-input"
                              type="date"
                              min={today}
                              defaultValue={defaultScheduleDateInput}
                              /* defaultValue={(() => {
                                const d = new Date(); d.setDate(d.getDate() + 1);
                                return d.toISOString().split("T")[0];
                              })()} */
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.value) {
                                  const d = new Date(e.target.value + "T00:00:00");
                                  setScheduleDate(d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }));
                                }
                              }}
                              style={{
                                flex: 1, background: "transparent", border: "none", outline: "none",
                                color: "#E8EAF0", fontSize: 12, cursor: "pointer",
                                padding: "9px 8px 9px 6px", width: "100%",
                              }}
                            />
                          </div>
                          {/* Time picker — direct visible input */}
                          <div style={{ ...S.dateInput, padding: 0, overflow: "hidden" }}>
                            <AccessTimeIcon sx={{ fontSize: 14, color: "#4BA5EB", flexShrink: 0, marginLeft: 10, cursor: 'default' }} />
                            <input
                              className="sched-date-input"
                              type="time"
                              defaultValue={defaultScheduleTimeInput}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.value) {
                                  const [h, m] = e.target.value.split(":");
                                  const d = new Date();
                                  d.setHours(parseInt(h), parseInt(m));
                                  setScheduleTime(d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }));
                                }
                              }}
                              style={{
                                flex: 1, background: "transparent", border: "none", outline: "none",
                                color: "#E8EAF0", fontSize: 12, cursor: "pointer",
                                padding: "9px 8px 9px 6px", width: "100%",
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(75,165,235,0.08)", border: "1px solid rgba(75,165,235,0.2)", borderRadius: 8, fontSize: 12, color: "#4BA5EB", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", overflow: "hidden", boxSizing: "border-box", width: "100%" }}>
                          <CalendarTodayIcon sx={{ fontSize: 13 }} />
                          Scheduled for <strong>{scheduleDate}</strong> at <strong>{scheduleTime}{scheduleTimezoneAbbr ? ` ${scheduleTimezoneAbbr}` : ""}</strong>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: "none" }}>
                <div style={S.sectionLabel}>3. Additional Options</div>

                <div style={S.optionCard(customizePerPlatform)} onClick={() => setCustomizePerPlatform(!customizePerPlatform)}>
                  <Checkbox
                    checked={customizePerPlatform}
                    onChange={() => setCustomizePerPlatform(!customizePerPlatform)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: "rgba(255,255,255,0.25)", "&.Mui-checked": { color: "#4BA5EB" }, padding: 0, marginTop: "2px" }}
                  />
                  <div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#E8EAF0" }}>
                        Customize content per platform
                      </span>
                      <span style={S.badge("#22c55e", "rgba(34,197,94,0.1)", "rgba(34,197,94,0.3)")}>
                        Recommended
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 3 }}>
                      Tailor your caption, title, and thumbnail for each platform.
                    </div>
                  </div>
                </div>

                <div style={S.optionCard(addFollowup)} onClick={() => setAddFollowup(!addFollowup)}>
                  <Checkbox
                    checked={addFollowup}
                    onChange={() => setAddFollowup(!addFollowup)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: "rgba(255,255,255,0.25)", "&.Mui-checked": { color: "#4BA5EB" }, padding: 0, marginTop: "2px" }}
                  />
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flex: 1 }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#E8EAF0" }}>
                          Add follow-up comment (after posting)
                        </span>
                        <span style={S.badge("#4BA5EB", "rgba(75,165,235,0.10)", "rgba(75,165,235,0.3)")}>New</span>
                      </div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 3 }}>
                        Automatically post a comment after your clip is published.
                      </div>
                    </div>
                    <Tooltip title="The follow-up comment will be posted 1–3 minutes after your clip is published.">
                      <InfoOutlinedIcon sx={{ fontSize: 15, color: "rgba(255,255,255,0.45)", flexShrink: 0, cursor: "pointer" }} />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>

            {/* ── RIGHT PANEL ── */}
            <div style={S.rightPanel}>
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <div style={S.sectionLabel}>3. Preview</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {isFetchingPost && <CircularProgress size={14} sx={{ color: "#4BA5EB" }} />}
                    {hasDraft && (
                      <div style={{ display: "flex", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 7, overflow: "hidden" }}>
                        {(["draft", "live"] as const).map((v) => (
                          <button
                            key={v}
                            onClick={() => switchVersion(v)}
                            style={{
                              border: "none", cursor: "pointer", padding: "4px 12px", fontSize: 11, fontWeight: 700,
                              background: versionView === v ? "rgba(75,165,235,0.18)" : "transparent",
                              color: versionView === v ? "#4BA5EB" : "rgba(255,255,255,0.5)",
                            }}
                          >
                            {v === "draft" ? "Draft" : "Live"}
                          </button>
                        ))}
                      </div>
                    )}
                    {!isEditingPreview ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
                        {showEditHint && (
                          <div style={{ display: "flex", alignItems: "center", gap: 4, animation: "fadeInHint 0.4s ease forwards", opacity: 1 }}>
                            <span style={{
                              fontSize: 11, fontWeight: 600, color: "#4BA5EB",
                              background: "rgba(75,165,235,0.08)", border: "1px solid rgba(75,165,235,0.25)",
                              borderRadius: 99, padding: "3px 9px", whiteSpace: "nowrap" as const, letterSpacing: "0.02em",
                            }}>
                              Click to edit preview
                            </span>
                            <span style={{ fontSize: 16, display: "inline-block", animation: "bounceLeft 1.2s ease-in-out infinite", color: "#4BA5EB" }}>👉</span>
                          </div>
                        )}
                        <Tooltip title="Edit title, description & video">
                          <IconButton
                            size="small"
                            onClick={() => { setIsEditingPreview(true); setShowEditHint(false); }}
                            disabled={isViewingLive}
                            sx={{
                              color: "#4BA5EB", border: "1.5px solid rgba(75,165,235,0.4)",
                              borderRadius: "7px", padding: "4px 8px",
                              animation: showEditHint ? "editBtnPulse 1.5s ease-in-out infinite" : "none",
                              "&:hover": { background: "rgba(75,165,235,0.08)" },
                            }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 14 }} />
                            <span style={{ fontSize: 11, color: "#4BA5EB", marginLeft: 3 }}>Edit</span>
                          </IconButton>
                        </Tooltip>
                      </div>
                    ) : (
                      <>
                        {/* <Tooltip title="Save changes">
                        <IconButton
                          size="small"
                          onClick={handleSaveEdit}
                          sx={{
                            color: "#22c55e", border: "1.5px solid rgba(34,197,94,0.4)",
                            borderRadius: "7px", padding: "4px 8px", height: 24,
                            "&:hover": { background: "rgba(34,197,94,0.08)" },
                          }}
                        >

                          {savingPost ? <CircularProgress size={14}
                            thickness={5} sx={{ color: '#22c55e' }} /> : <>
                            <SaveOutlinedIcon sx={{ fontSize: 14 }} />
                            <span style={{ fontSize: 11, color: "#22c55e", marginLeft: 3 }}>Save</span>
                          </>}
                        </IconButton>
                      </Tooltip> */}
                      </>
                    )}
                    {isEditingPreview && (
                      <Tooltip title="Cancel editing">
                        <IconButton
                          size="small"
                          onClick={() => setIsEditingPreview(false)}
                          sx={{
                            color: "rgba(255,255,255,0.55)", border: "1.5px solid rgba(255,255,255,0.1)",
                            borderRadius: "7px", padding: "4px 8px",
                            "&:hover": { background: "rgba(255,255,255,0.04)" },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>
                <div style={S.sectionSub}>This is how your clip will look on each platform.</div>
                {hasDraft && (
                  <div style={{
                    fontSize: 12, lineHeight: 1.5, borderRadius: 8, padding: "8px 12px", margin: "8px 0 12px",
                    color: isViewingLive ? "#d6b25e" : "#7fc2f5",
                    background: isViewingLive ? "rgba(214,178,94,0.08)" : "rgba(75,165,235,0.08)",
                    border: `1px solid ${isViewingLive ? "rgba(214,178,94,0.3)" : "rgba(75,165,235,0.3)"}`,
                  }}>
                    {isViewingLive
                      ? "👁 You're viewing the live version (read-only). Switch to Draft to edit or publish your changes."
                      : "📝 Draft — your saved changes are not live yet. The live post is unchanged until you publish."}
                  </div>
                )}

                <div style={{ display: "flex", gap: 4, borderBottom: "1px solid rgba(255,255,255,0.07)", marginBottom: 16 }}>
                  {PREVIEW_TABS.filter((tab) => selectedPlatforms.includes(tab.id)).map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setPreviewTab(tab.id)}
                      style={{
                        background: "none", border: "none", padding: "8px 14px", fontSize: 12,
                        fontWeight: previewTab === tab.id ? 700 : 500,
                        color: previewTab === tab.id ? "#4BA5EB" : "rgba(255,255,255,0.45)",
                        cursor: "pointer",
                        borderBottom: previewTab === tab.id ? "2px solid #4BA5EB" : "2px solid transparent",
                        transition: "all 0.15s",
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {isEditingPreview && (
                  <div style={{
                    background: "#15151D", border: "1.5px solid rgba(255,255,255,0.1)",
                    borderRadius: 12, padding: "16px", marginBottom: 16,
                    display: "flex", flexDirection: "column", gap: 12,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#4BA5EB", marginBottom: 2 }}>
                      ✏️ Edit Preview Content
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 5, fontWeight: 600 }}>Title</div>
                      <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="Enter post title..." style={{ ...S.editInput, padding: "9px 12px" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "rgba(46, 19, 19, 0.45)", marginBottom: 5, fontWeight: 600 }}>Caption / Description</div>
                      <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} placeholder="Enter post caption..." rows={4} style={{ ...S.editInput, resize: "vertical", lineHeight: 1.6 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 5, fontWeight: 600 }}>Thumbnail URL</div>

                      {editThumbnailUrl && (
                        <div style={{ marginTop: 8 }}>
                          <img
                            src={editThumbnailUrl}
                            alt="Thumbnail"
                            title="Click here to edit image"
                            style={{
                              width: "100%",
                              maxWidth: 220,
                              height: 120,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid rgba(255,255,255,0.12)",
                            }}
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = "none";
                            }}
                            onClick={() => setChoosingImageModal(true)}
                          />
                        </div>
                      )}

                      <Button
                        size="small"
                        onClick={() => setChoosingImageModal(true)}
                        startIcon={<EditOutlinedIcon />}
                        sx={{ mt: 1 }}
                      >
                        Edit Image
                      </Button>
                    </div>
                    {availableImages.length > 1 && (
                      <div>
                        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 5, fontWeight: 600 }}>
                          More images ({extraImages.length} selected) · posted together on Facebook &amp; LinkedIn
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {availableImages.filter((u) => u !== (editThumbnailUrl || clipThumbnail)).map((u) => {
                            const on = extraImages.includes(u);
                            return (
                              <div key={u} onClick={() => toggleExtraImage(u)} title={on ? "Remove from post" : "Add to post"}
                                style={{
                                  position: "relative", width: 72, height: 72, borderRadius: 8, overflow: "hidden", cursor: "pointer",
                                  border: on ? "2px solid #4BA5EB" : "1px solid rgba(255,255,255,0.15)", opacity: on ? 1 : 0.55,
                                }}>
                                <img src={u} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                {on && (
                                  <span style={{
                                    position: "absolute", top: 4, right: 4, minWidth: 18, height: 18, borderRadius: 9,
                                    background: "#4BA5EB", color: "#fff", fontSize: 11, fontWeight: 700,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                  }}>{extraImages.indexOf(u) + 2}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>
                          The thumbnail is image 1. Click images to add or remove them.
                        </div>
                      </div>
                    )}
                    {sourceType !== 'blog' && sourceType !== 'course' && (<div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 5, fontWeight: 600 }}>Video</div>
                      {editVideoUrl && (
                        <video
                          src={getFullVideoUrl(editVideoUrl)}
                          controls
                          style={{
                            width: "100%",
                            borderRadius: 10,
                            marginTop: 10,
                          }}
                        />
                      )}

                      {/* {isEditingPreview && ( */}
                      <Button
                        size="small"
                        onClick={() => videoInputRef.current?.click()}
                        startIcon={<EditOutlinedIcon />}
                        sx={{ mt: 1 }}
                      >
                        {editVideoUrl ? "Replace Video" : 'Add Video'}
                      </Button>
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        hidden
                        onChange={handleVideoChange}
                      />
                      {/* )} */}
                    </div>)}
                    {sourceType === 'blog' && (<div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 5, fontWeight: 600 }}>
                        {sourceType === 'course' ? 'Course URL' : sourceType === 'clip' ? 'Clip URL' : sourceType === 'episode' ? 'Episode URL' : 'Blog URL'}
                      </div>
                      <input
                        value={editBlogUrl}
                        readOnly
                        onChange={(e) => setEditBlogUrl(e.target.value)}
                        placeholder={editBlogUrl || (sourceType === 'course' ? 'Course URL will appear here...' : sourceType === 'clip' ? 'Clip URL will appear here...' : sourceType === 'episode' ? 'Episode URL will appear here...' : 'Blog URL will appear here...')}
                        style={{ ...S.editInput, padding: "9px 12px" }}
                      />
                      {editBlogUrl && (
                        <div style={{ fontSize: 10, color: "#4BA5EB", marginTop: 4 }}>
                          🔗 Will appear as a clickable link in preview.
                        </div>
                      )}
                    </div>)}

                    {/* ── Blog Suggestions ── */}
                    {/* {sourceType === 'blog' && (<div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", fontWeight: 600 }}>
                          {sourceType === 'course' ? 'Course Suggestions' : sourceType === 'clip' ? 'Clip Suggestions' : sourceType === 'episode' ? 'Episode Suggestions' : 'Blog Suggestions'}
                        </div>
                        <button
                          onClick={fetchBlogSuggestions}
                          disabled={isFetchingBlogs}
                          style={{
                            background: "rgba(75,165,235,0.1)", border: "1px solid rgba(75,165,235,0.3)",
                            borderRadius: 6, color: "#4BA5EB", fontSize: 11, fontWeight: 600,
                            padding: "4px 10px", cursor: isFetchingBlogs ? "not-allowed" : "pointer",
                            display: "flex", alignItems: "center", gap: 5, opacity: isFetchingBlogs ? 0.6 : 1,
                          }}
                        >
                          {isFetchingBlogs ? (
                            <><CircularProgress size={10} sx={{ color: "#4BA5EB" }} /> Loading...</>
                          ) : (
                            <>{showBlogSuggestions ? "↻ Refresh" : sourceType === 'course' ? "✦ Suggest from Course" : sourceType === 'clip' ? "✦ Suggest from Clips" : sourceType === 'episode' ? "✦ Suggest from Episode" : "✦ Suggest from Blogs"}</>
                          )}
                        </button>
                      </div>

                      {showBlogSuggestions && blogSuggestions.length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 260, overflowY: "auto" }}>
                          {blogSuggestions.map((s: any, idx: number) => {
                            const key = s.blog_id + '_' + (s.content_angle || idx);
                            const isActive = selectedBlogId === key;
                            const platformKey = (previewTab === 'authority' ? 'linkedin' : previewTab) as string;
                            const previewText = s.adaptation?.[platformKey] || s.hook || '';
                            return (
                              <div
                                key={key}
                                onClick={() => handleSelectBlogSuggestion(s)}
                                style={{
                                  padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                                  border: isActive ? "1.5px solid #4BA5EB" : "1px solid rgba(255,255,255,0.08)",
                                  background: isActive ? "rgba(75,165,235,0.08)" : "rgba(255,255,255,0.02)",
                                  transition: "all 0.15s",
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: isActive ? "#4BA5EB" : "#E8EAF0" }}>
                                    {s.title}
                                  </span>
                                  <div style={{ display: "flex", gap: 4 }}>
                                    <span style={{
                                      fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 99,
                                      background: s.estimated_engagement === "high" ? "rgba(34,197,94,0.15)" : "rgba(245,158,11,0.15)",
                                      color: s.estimated_engagement === "high" ? "#22c55e" : "#f59e0b",
                                      border: s.estimated_engagement === "high" ? "1px solid rgba(34,197,94,0.3)" : "1px solid rgba(245,158,11,0.3)",
                                      textTransform: "uppercase" as const,
                                    }}>
                                      {s.estimated_engagement}
                                    </span>
                                    <span style={{
                                      fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 99,
                                      background: "rgba(75,165,235,0.12)", color: "#4BA5EB",
                                      border: "1px solid rgba(75,165,235,0.25)", textTransform: "uppercase" as const,
                                    }}>
                                      {s.content_angle}
                                    </span>
                                  </div>
                                </div>
                                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
                                  {previewText}
                                </div>
                                {(s.blog_url || s.course_url || s.clip_url || s.episode_url) && (
                                  <div style={{
                                    fontSize: 10, color: "#4BA5EB", marginTop: 4,
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const,
                                  }}>
                                    🔗 {(s.blog_url || s.course_url || s.clip_url || s.episode_url || '').replace(/\/\/$/, '')}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {showBlogSuggestions && blogSuggestions.length === 0 && !isFetchingBlogs && (
                        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", textAlign: "center", padding: "12px 0" }}>
                          No {sourceType === 'course' ? 'course' : sourceType === 'clip' ? 'clip' : sourceType === 'episode' ? 'episode' : 'blog'} suggestions found.
                        </div>
                      )}
                    </div>)} */}
                  </div>
                )}

                {!isEditingPreview && (<div style={{ /* maxHeight: "calc(100vh - 340px)", overflowY: "auto", */ borderRadius: 14, scrollbarWidth: "thin", scrollbarColor: "rgba(75,165,235,0.3) transparent" }}>
                  <div style={S.previewCard}>
                    <div style={S.previewHeader}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={S.previewAvatar}>{initials}</div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#E8EAF0" }}>{resolvedUserName}</div>
                          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", display: "flex", alignItems: "center", gap: 4 }}>
                            Just now · 🌐
                          </div>
                        </div>
                      </div>
                      <MoreHorizIcon sx={{ color: "rgba(255,255,255,0.45)", fontSize: 20, cursor: "pointer" }} />
                    </div>

                    <div style={{ padding: "0 16px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                      {displayTitle && (
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#E8EAF0", lineHeight: 1.4 }}>
                          {displayTitle}
                        </div>
                      )}
                      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                        {getPlatformDescription()}
                      </div>
                      {editBlogUrl && (
                        <a
                          href={editBlogUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            background: "rgba(75,165,235,0.08)",
                            border: "1px solid rgba(75,165,235,0.25)",
                            borderRadius: 8, padding: "6px 10px",
                            fontSize: 12, color: "#4BA5EB",
                            textDecoration: "none", fontWeight: 500,
                            maxWidth: "100%", overflow: "hidden",
                            wordBreak: "break-all" as const,
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(75,165,235,0.15)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(75,165,235,0.08)")}
                        >
                          🔗 {editBlogUrl}
                        </a>
                      )}
                      {navState.cta_url && (
                        // SHOPLINE product post: link to the product page in the store
                        <a
                          href={navState.cta_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            alignSelf: "flex-start",
                            display: "inline-flex", alignItems: "center", gap: 6,
                            background: "#4BA5EB", color: "#fff",
                            borderRadius: 8, padding: "8px 16px",
                            fontSize: 13, fontWeight: 600, textDecoration: "none",
                          }}
                        >
                          {navState.cta_label || "More Details"} →
                        </a>
                      )}
                    </div>

                    <div style={S.videoThumb} onClick={() => setIsPlaying(!isPlaying)}>
                      {displayVideoUrl ? (
                        previewTab === "youtube" && toYouTubeEmbed(getRawVideoUrl()) ? (
                          <iframe
                            width="100%" height="100%"
                            src={toYouTubeEmbed(getRawVideoUrl(), { rel: false, modestbranding: true }) ?? ""}
                            title="YouTube preview"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : /\.(mp4|webm|ogg|mov)(\?|$)/i.test(displayVideoUrl) ? (
                          <video
                            key={displayVideoUrl}
                            src={`${displayVideoUrl}#t=0.1`}
                            poster={editThumbnailUrl || clipThumbnail || undefined}
                            preload="metadata"
                            playsInline
                            onError={(e) => console.error("Preview video failed to load:", displayVideoUrl, e.currentTarget.error)}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            controls={isPlaying}
                            autoPlay={isPlaying}
                            onEnded={() => setIsPlaying(false)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <img
                            src={displayVideoUrl}
                            alt={displayTitle}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        )
                      ) : (
                        <div style={{
                          position: "absolute", inset: 0,
                          background: "linear-gradient(135deg, #0F1117 0%, #15151D 50%, #0F1117 100%)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <div style={{
                            position: "absolute", left: 24, top: "30%",
                            fontSize: 22, fontWeight: 900, color: "#E8EAF0",
                            lineHeight: 1.2, textTransform: "uppercase", letterSpacing: "-0.5px",
                          }}>
                            {displayTitle.split(" ").reduce((acc: string[][], word: string, i: number) => {
                              if (i % 3 === 0) acc.push([]);
                              acc[acc.length - 1].push(word);
                              return acc;
                            }, [] as string[][]).map((line: string[], li: number) => (
                              <div key={li} style={{ color: li === 2 ? "#4BA5EB" : "#E8EAF0" }}>{line.join(" ")}</div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!isPlaying && /\.(mp4|webm|ogg|mov)(\?|$)/i.test(displayVideoUrl) && previewTab !== "youtube" && (
                        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
                          <div style={{
                            width: 56, height: 56, borderRadius: "50%",
                            background: "rgba(75,165,235,0.15)", border: "2px solid rgba(75,165,235,0.4)",
                            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                          }}>
                            <PlayArrowIcon sx={{ fontSize: 28, color: "#4BA5EB" }} />
                          </div>
                        </div>
                      )}

                      {!isPlaying && previewTab !== "youtube" && (
                        <div style={{ position: "absolute", bottom: 10, right: 12, fontSize: 11, fontWeight: 700, color: "#ffffff", background: "rgba(0,0,0,0.55)", padding: "2px 7px", borderRadius: 4, zIndex: 3 }}>
                          0:06
                        </div>
                      )}

                      {!isPlaying && previewTab !== "youtube" && (
                        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "rgba(255,255,255,0.15)", zIndex: 3 }}>
                          <div style={{ width: "30%", height: "100%", background: "#4BA5EB" }} />
                        </div>
                      )}
                    </div>
                    {extraImages.length > 0 && !editVideoUrl && (
                      // Multi-image post: the extra images after the cover
                      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(extraImages.length, 4)}, 1fr)`, gap: 2, marginTop: 2 }}>
                        {extraImages.slice(0, 4).map((u, k) => (
                          <div key={u} style={{ position: "relative", paddingTop: "100%", overflow: "hidden" }}>
                            <img src={u} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                            {k === 3 && extraImages.length > 4 && (
                              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)", color: "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700 }}>
                                +{extraImages.length - 4}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}




                  </div>
                </div>)}
              </div>

              {/* {!isEditingPreview && (<div style={S.followupCard}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#E8EAF0" }}>4. Follow-up Comment</span>
                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>(After Posting)</span>
                    <span style={S.badge("#4BA5EB", "rgba(75,165,235,0.10)", "rgba(75,165,235,0.3)")}>New</span>
                  </div>
                  <Switch
                    checked={followupEnabled}
                    onChange={() => setFollowupEnabled(!followupEnabled)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#4BA5EB" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { background: "#4BA5EB" },
                    }}
                  />
                </div>

                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginBottom: 14 }}>
                  Engage Your audience by posting a comment automatically after your clip is published.
                </div>

                {followupEnabled && (
                  <div style={{ display: "flex", gap: 10 }}>
                    <div style={S.followupOption(followupType === "ai")} onClick={() => setFollowupType("ai")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${followupType === "ai" ? "#4BA5EB" : "rgba(255,255,255,0.2)"}`, background: followupType === "ai" ? "#4BA5EB" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {followupType === "ai" && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff" }} />}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#E8EAF0" }}>AI Generated Comment</span>
                        <span style={S.badge("#22c55e", "rgba(34,197,94,0.1)", "rgba(34,197,94,0.3)")}>Recommended</span>
                      </div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>
                        Let AI create an engaging comment based on your clip.
                      </div>
                    </div>

                    <div style={{ flex: 1, padding: 14, background: "#15151D", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.65)", marginBottom: 8 }}>
                        Preview of AI Generated Comment
                      </div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", lineHeight: 1.6 }}>
                        Great point! AI is already transforming the way we work — how are you using AI in your workflow? 👇
                      </div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 8 }}>
                        This will be posted 1–3 minutes after your clip.
                      </div>
                    </div>
                  </div>
                )}

                {followupEnabled && (
                  <div style={{ ...S.followupOption(followupType === "custom"), marginTop: 10, padding: "12px 14px" }} onClick={() => setFollowupType("custom")}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${followupType === "custom" ? "#4BA5EB" : "rgba(255,255,255,0.2)"}`, background: followupType === "custom" ? "#4BA5EB" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {followupType === "custom" && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff" }} />}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#E8EAF0" }}>Custom Comment</span>
                    </div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 4, marginLeft: 22 }}>
                      Write your own comment to post.
                    </div>
                    {followupType === "custom" && (
                      <textarea
                        placeholder="Write your follow-up comment..."
                        rows={3}
                        style={{ width: "100%", marginTop: 10, background: "rgba(255,255,255,0.04)", border: "1.5px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "10px 12px", color: "#E8EAF0", fontSize: 12, resize: "vertical", outline: "none", boxSizing: "border-box" }}
                      />
                    )}
                  </div>
                )}
              </div>)} */}
            </div>
          </div>

          {/* DISCLAIMER */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mt: 2,
              mb: 1.5,
              px: 2,
              py: 1.2,
              borderRadius: "10px",
              background: "rgba(250,204,21,0.08)",
              border: "1px solid rgba(88, 88, 86, 0.25)",
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#c2bfb0", fontWeight: 500 }}>
              ⚠️ AI-generated content may contain errors. Please review and verify your content before publishing.
            </Typography>
          </Box>

          <div style={S.bottomBar}>
            {apiError && (
              <div style={{ fontSize: 12, color: "#e74c3c", flex: 1, textAlign: "left" }}>
                {apiError}
              </div>
            )}
            <button /* style={S.cancelBtn} */ style={{
              ...S.cancelBtn,
              opacity: isEditingPreview ? 0.45 : 1,
              cursor: isEditingPreview ? "not-allowed" : "pointer",
              filter: isEditingPreview ? "grayscale(.5)" : "none",
            }}
              disabled={isEditingPreview}
              onClick={handleClose}>Cancel</button>

            {isEditingPreview && (
              <button
                style={{
                  color: "#fff", border: "1.5px solid rgba(34,197,94,0.4)",
                  borderRadius: "7px", padding: "9px 20px",
                  background: '#198754 ', fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center"
                }}
                onClick={handleSaveEdit}
                disabled={savingPost}
              >
                {savingPost ? <CircularProgress size={14}
                  thickness={5} sx={{ color: '#fff' }} /> : <>
                  <SaveOutlinedIcon sx={{ fontSize: 14 }} />
                  <span style={{ fontSize: 11, color: "#fff", marginLeft: 3 }}>Save</span>
                </>}
              </button>
            )}

            <div style={{ position: "relative" }}>
              <div style={{ display: "flex" }}>
                <button
                  style={{ ...S.publishBtn, border: "none", cursor: isLoading || isEditingPreview ? "not-allowed" : "pointer", opacity: isLoading || isEditingPreview ? 0.7 : 1, filter: isEditingPreview ? "grayscale(.5)" : "none" }}
                  onClick={handlePublish}
                  disabled={isLoading || isEditingPreview || isViewingLive}
                >
                  <SendOutlinedIcon sx={{ fontSize: 15 }} />
                  {isLoading ? "Processing..." : timing === "now" ? "Publish Now" : "Schedule"}
                </button>
                <button
                  style={{ ...S.publishDropBtn, border: "none", cursor: isEditingPreview ? "not-allowed" : "pointer", borderLeft: "1px solid rgba(255,255,255,0.2)", opacity: isEditingPreview ? 0.45 : 1, filter: isEditingPreview ? "grayscale(.5)" : "none", }}
                  onClick={() => setShowDropMenu(!showDropMenu)}
                  disabled={isEditingPreview || isViewingLive}
                >
                  <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#fff" }} />
                </button>
              </div>

              {showDropMenu && (
                <div style={S.dropMenu}>
                  {[
                    { label: "Schedule", icon: "🗓", action: () => { setTiming("later"); setShowDropMenu(false); } },
                    { label: "Save as Draft", icon: "📄", action: () => { setShowDropMenu(false); toast.info("Saved as draft!"); } },
                  ].map((item) => (
                    <div
                      key={item.label}
                      style={S.dropMenuItem}
                      onClick={item.action}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.06)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span>{item.icon}</span>
                      {item.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      <AIConciergePopup
        open={openAIConcierge}
        onClose={() => setOpenAIConcierge(false)}
        courseid={"6a155659b7d74efa94037f50"}
      />

      <ComingSoonPopup
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
        title="Coming Soon"
        description={`${comingSoonPlatform} feature is almost ready. We're putting on the finishing touches — stay tuned!`}
      />

      <Dialog
        open={upgradeModal.open}
        onClose={() =>
          setUpgradeModal((prev) => ({ ...prev, open: false }))
        }
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            background: "#121212",
            color: "#fff",
            p: 1,
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <AutoAwesomeIcon
            sx={{
              fontSize: 50,
              color: "#F59E0B",
              mb: 2,
            }}
          />

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 1,
            }}
          >
            {upgradeModal.title}
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 14,
              mb: 4,
            }}
          >
            {upgradeModal.message}
          </Typography>

          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() =>
                setUpgradeModal((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
              sx={{
                textTransform: "none",
                borderColor: "rgba(255,255,255,0.2)",
                color: "#fff",
              }}
            >
              Cancel
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                const value = {
                  _id: "",
                  product_name: "CodeGenie",
                  product_module: genieSelector.module || "",
                  userid: "",
                  productid: "",
                };

                getProduct(value);

                localStorage.setItem("fromUpgrade", "true");

                setUpgradeModal({
                  open: false,
                  title: "",
                  message: "",
                });

                navigate("/pricing", {
                  state: {
                    campIdFromUpgrade: selectedCampaign,
                    from_page: "pricing",
                    fromNonSubscribed: "true",
                  },
                });
              }}
              sx={{
                textTransform: "none",
                background: "linear-gradient(90deg,#0168B5 0%,#4BA5EB 100%)",
                "&:hover": {
                  background: "linear-gradient(90deg,#0168B5 0%,#4BA5EB 100%)",
                },
              }}
            >
              Upgrade Plan
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* preview image editing based on local and unsplash */}
      <Modal
        open={choosingImageModal}
        onClose={() => setChoosingImageModal(false)}
        disableEscapeKeyDown
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            transform: "translate(-50%, -50%)",
            width: isMobile ? "95%" : "40%",
            bgcolor: "#161b22",
            // border: "2px solid #000",
            // boxShadow: 24,
            boxShadow: '0px 50px 50px rgba(0, 0, 0, 0.5)',
            pt: 2,
            px: 4,
            pb: 3,
            borderRadius: '15px',
            padding: '3%'
          }}
        >
          <IconButton
            onClick={() => setChoosingImageModal(false)}
            sx={{
              position: 'absolute',
              top: '6px',
              right: '7px',
              color: '#fff',
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography style={{ textAlign: 'center', fontWeight: 'bold' }}>{/* <InfoIcon sx={{ color: '#ffcc00' }} /><br /> */}Choose your image from below options</Typography>

          <Stack flexDirection="row" sx={{ display: 'flex', gap: '20px', marginTop: '5%' }}>

            {/*  <Button
                            variant="contained"
                            onClick={() => handleDelete(deleteBlogId)}>
                            Yes
                        </Button> */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} onClick={handleImageClick}>
              <ComputerIcon sx={{ fontSize: '3rem' }} />
              <p style={{ color: '#fff' }}>Choose from folder</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ /* width: '100%', */ display: 'none' }}
                onChange={handleImageChange}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <img src={unsplashImg} style={{ width: '28%', height: '48px',/* , marginTop: '2%' */ background: 'transparent' }} onClick={() => setOpenUnsplash(true)} />
              <p style={{ color: '#fff' }}>Choose from Unsplash</p>
            </div>
            {/* <Button
                            onClick={() => setDeleteModal(false)}
                            variant="contained"
                        sx={{ mr: 2, marginTop: '15%' }}
                        >
                            No
                        </Button> */}
          </Stack>
        </Box>
      </Modal>

      <Modal
        open={openUnsplash}
        onClose={() => setOpenUnsplash(false)}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            transform: "translate(-50%, -50%)",
            width: isMobile ? "90%" : "54%",
            bgcolor: "#161b22",
            // border: "2px solid #000",
            // boxShadow: 24,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 1.2)',
            pt: 2,
            px: 4,
            pb: 3,
            borderRadius: '10px',
            overflowY: isMobile ? 'auto' : 'hidden',
            height: isMobile ? '600px' : '650px',
            maxHeight: '650px'
          }}
        >
          <div className="row" style={{ width: '100%', marginTop: '5%' }}>
            <div className=" col-sm-12 col-md-10 col-lg-10">
              <TextField
                placeholder="Search Unsplash images"
                size="small"
                InputLabelProps={{
                  className: 'red-label'
                }}
                InputProps={{
                  /*  ...params.InputProps, */
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    color: 'white',
                    opacity: 1
                  },
                  width: '100%',
                  '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#30363d' }, '&:hover fieldset': { borderColor: '#3b82f6' }, '&.Mui-focused fieldset': { borderColor: '#3b82f6' }, backgroundColor: '#0d1117' },
                  '& .MuiInputBase-input': { color: '#e5e7eb' },
                  '& .MuiSvgIcon-root': { color: '#6b7280' }
                }}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div className="col-sm-12 col-md-2 col-lg-2" style={{ marginTop: isMobile ? '2%' : '' }}>
              <Button variant="contained" onClick={handleSearch} sx={{ fontWeight: 'bold', width: isMobile ? '100%' : '' }}>Search</Button>
            </div>
            {/* <div className="col-md-8">
                        <Typography component="div">
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search Unsplash images"
                            style={{
                                width: '100%',
                                padding: '10px',
                                fontSize: '1rem',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                marginBottom: '1rem',
                            }}
                        />
                        </Typography>
                        </div>
                        <div className="col-md-4">
                        <Button variant="contained" onClick={handleSearch}>Search</Button>
                    </div> */}
          </div>
          {unsplashSearch === false ?
            <div className="row" style={{ width: '100%' }}>
              <div className="col-md-12">
                <img src={unsplash_logo} style={{ width: '10%', display: 'block', margin: '0 auto', marginTop: '17%' }}></img>
                <Typography variant="h4" sx={{ marginTop: '1%', textAlign: 'center', color: '#e5e7eb' }}>Unsplash</Typography>
                <Typography variant="h6" sx={{ marginTop: '1%', textAlign: 'center' }}>Try entering a search term, such as apple or daisy, to explore beautiful, high-resolution visuals.</Typography>{/* <br></br> */}

                <Typography variant="h6" sx={{ textAlign: 'center' }}>Once you've found an image you're happy with, click on it to add it to your workspace.</Typography>
                <Typography variant="h6" sx={{ textAlign: 'center' }} onClick={() => window.open('https://unsplash.com/', '_blank')}><a href="" style={{ textDecoration: 'none' }}>unsplash.com</a></Typography>
              </div>
            </div>
            :
            <div className="row" style={{ marginTop: '6%' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)',
                gap: '10px',
                justifyContent: 'center'
              }}/* style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }} */>
                {horizontalImages.map((image: any) => (
                  <div key={image.id} className="thumbnail-container">
                    <img
                      className="thumbnail"
                      key={image.id}
                      src={modifyImageUrl(image.urls.thumb)}
                      alt={image.alt_description}
                      style={{ width: '100px', /* height: '100px', */ cursor: 'pointer', border: image.id === selectedUnsplashImg ? '3px solid #3b82f6' : '' }}
                      onClick={() => handleSelectUnsplashImage(image.urls.regular, image.id)}
                    />
                    <img
                      className="full-image"
                      src={image.urls.regular}
                      alt={image.alt_description}
                    />
                  </div>
                ))}
              </div>
            </div>
          }

          <div className="row" style={{ marginTop: '5%' }}>
            <div className={isMobile ? "col-6" : "col-sm-6 col-md-6 col-lg-6"}>
              <Button variant="contained" sx={{ fontWeight: 'bold' }} onClick={handleUnsplashselectionChange/* () => setOpenUnsplash(false) */}>Select</Button>
            </div>
            <div className={isMobile ? "col-6" : "col-sm-6 col-md-6 col-lg-6"}>
              <Button variant="contained" sx={{ fontWeight: 'bold' }} onClick={() => setOpenUnsplash(false)}>Close</Button>
            </div>
          </div>
        </Box>
      </Modal>
      {/* end preview image editing based on local and unsplash */}
    </>
  );
};

export default PublishScheduleClip;